
export interface DashboardStats {
  pendingListings: number;
  /** Unresolved driver verification reviews (licence and MVR). */
  pendingDriverReviews?: number;
  activeTrips: number;
  revenueToday: number;
  newUsers: number;
  /** Pending host-submitted brands and the age of the oldest queue item. */
  pendingBrandRequests?: number;
  oldestPendingBrandRequestAt?: string | null;
  oldestPendingBrandRequestAgeHours?: number | null;
  brandRequestStaleAfterHours?: number;
  flaggedMessages: number;
  disputes: number;
  flaggedHostProfiles: number;
  /** Task #606: host profiles awaiting admin moderation (flagged or pending_manual_review). */
  pendingProfileModeration: number;
  /** Task #550/#5598: pending request bookings whose 48h window expires within 12h. */
  requestsAtRisk: number;
  /** Count of active listings with Instant Book enabled (instant_rental = true). */
  instantBookCars: number;
  /**
   * Pending host-cancellation requests whose linked trip is still eligible for
   * admin review. Optional to tolerate a short rolling deploy window.
   */
  pendingHostCancellations?: number;
  /**
   * Flagged conversations escalated for senior review with no later terminal
   * decision (RPC view 'escalations', migration 3138). Optional so the
   * dashboard tolerates an older API build during deploys.
   */
  escalatedMessages?: number;
  /**
   * Cars (listings) created today, for the "Today's pulse" strip. Optional so
   * the dashboard tolerates an older API build during deploys.
   */
  newCarsToday?: number;
  /**
   * Task #6805 — hosts currently offline due to an unanswered booking request
   * (latest audit_log marker is expiry_auto_pause with no subsequent
   * expiry_auto_unpause). Optional so the dashboard tolerates an older API
   * build during deploys.
   */
  autoPausedHostsActive?: number;
  /**
   * Task #6805 — hosts who were auto-paused and have since self-recovered
   * (clicked their listings back online) within the last 30 days. Optional so
   * the dashboard tolerates an older API build during deploys.
   */
  autoPausedHostsRecovered?: number;
}


export interface PendingHostApprovalScorecard {
  lifetimeTrips: number;
  acceptedCount: number;
  declinedCount: number;
  expiredCount: number;
  acceptanceRate: number | null;
  responseRate: number | null;
  pendingNow: number;
  firstTimeHost: boolean;
  inactive: boolean;
  lastLoginAt: string | null;
  hoursSinceLastLogin: number | null;
  allStar: boolean;
}


export interface PendingHostApprovalRenterWait {
  lifetimeTrips: number;
  hoursWaiting: number;
  minutesWaiting: number;
  seeingPendingScreen: boolean;
}


export interface PendingHostApprovalRisk {
  score: number;
  level: "low" | "medium" | "high";
  reasons: { label: string; weight: number }[];
}


export type PendingHostApprovalViewSlug =
  | "expiring_soon"
  | "inactive_hosts"
  | "first_time_hosts"
  | "high_value_renters";


export interface PendingHostApprovalRow {
  id: number;
  publicId: string;
  startDate: string | null;
  endDate: string | null;
  totalAmount: number | null;
  currency: string | null;
  createdAt: string;
  offerExpirationDate: string | null;
  hoursElapsed: number;
  minutesElapsed: number;
  hoursRemaining: number;
  minutesRemaining: number;
  atRisk: boolean;
  autoCancelAt: string | null;
  car: { id: number; label: string } | null;
  host: { id: string; name: string; email: string | null; scorecard: PendingHostApprovalScorecard } | null;
  renter: { id: string; name: string; email: string | null; wait: PendingHostApprovalRenterWait } | null;
  reminders: { reminder_type: string; sent_at: string; sent_to: string; sent_by: string | null }[];
  risk: PendingHostApprovalRisk;
  viewSlugs: PendingHostApprovalViewSlug[];
}


export interface PendingHostApprovalsResponse {
  items: PendingHostApprovalRow[];
  page: number;
  pageSize: number;
  total: number;
}


export interface PendingHostApprovalsSummary {
  total: number;
  expiringSoon: number;
  atRisk: number;
  highValueRenters: number;
  inactiveHosts: number;
  firstTimeHosts: number;
}


export type PendingHostApprovalRiskFilter = "all" | "at_risk" | "stale" | "fresh";

export type PendingHostApprovalSort = "expires_asc" | "expires_desc" | "created_asc" | "created_desc" | "value_asc" | "value_desc";

export type PendingHostApprovalNudgeChannel = "in_app" | "email" | "sms" | "push";


export interface PendingHostApprovalListParams {
  page?: number;
  pageSize?: number;
  risk?: PendingHostApprovalRiskFilter;
  view?: PendingHostApprovalViewSlug | null;
  q?: string;
  sort?: PendingHostApprovalSort;
}


export interface PendingHostApprovalNudgePayload {
  templateId?: number | null;
  channels?: PendingHostApprovalNudgeChannel[];
  subject?: string | null;
  body?: string | null;
}


export interface PendingHostApprovalNudgeResult {
  ok: true;
  hoursRemaining: number;
  delivered: PendingHostApprovalNudgeChannel[];
  skipped: { channel: PendingHostApprovalNudgeChannel; reason: string }[];
}


export interface PendingHostApprovalBulkNudgeResult {
  ok: true;
  succeeded: number;
  failed: { tripId: number; ok: false; error: string }[];
  results: { tripId: number; ok: boolean; delivered?: PendingHostApprovalNudgeChannel[]; error?: string }[];
}


export interface ChartData {
  bookings: { date: string; count: number }[];
  revenue: { date: string; amount: number }[];
  /**
   * Task #6516 — count of trips whose host approved_at falls on each day.
   * Stacked alongside `bookings` in the Daily Bookings bar chart to show
   * the confirmed funnel. Optional so the dashboard tolerates an older API
   * build during deploys.
   */
  confirmedBookings?: { date: string; count: number }[];
  /**
   * Task #6516 — total expected trip value (payment_due / total_price /
   * rental_fee) summed by created_at day. Rendered as an outline series
   * over the confirmed revenue area in the Daily Revenue chart. Optional
   * so the dashboard tolerates an older API build during deploys.
   */
  bookedValue?: { date: string; amount: number }[];
  /**
   * Trips created that day that left the funnel without converting
   * (cancelled/declined/expired). Stacked with `bookings` + `confirmedBookings`
   * the bar totals ALL trips created that day. Optional for deploy tolerance.
   */
  lapsedBookings?: { date: string; count: number }[];
  /**
   * Expected value of trips created that day that are STILL open
   * (pending/approved) — the live-pipeline slice of `bookedValue`.
   * Optional for deploy tolerance.
   */
  openValue?: { date: string; amount: number }[];
}

/**
 * Task #6071 — last-24h booking-status summary boxes shown inside the
 * Daily Revenue / Daily Bookings dashboard cards. Rolling 24h window,
 * migrated/legacy trips excluded server-side. Revenue is real succeeded
 * charges where they exist, else the trip's stored total value.
 */
export type DashboardStatusSummaryKey = "booked" | "confirmed" | "cancelled" | "declined" | "won_back";

export interface DashboardStatusSummary {
  since: string;
  statuses: Record<DashboardStatusSummaryKey, { count: number; revenue: number }>;
}


export interface AdminListing {
  id: number;
  slug: string | null;
  country_code: string | null;
  make: string;
  model: string;
  year: number;
  listing_status: string;
  created_at: string;
  submitted_at: string | null;
  city: string | null;
  state: string | null;
  owner: { id: string; first_name: string; last_name: string; email: string } | null;
  images: { id: number; url: string | null; r2_path: string | null; image: string | null; cdn_thumbnail: string | null; cdn_medium: string | null; cdn_full: string | null; is_primary: boolean }[];
  // Task #2256 — true when this listing has an open held-change row.
  pending_change?: boolean;
  /**
   * Oldest open pending_manual_review hold on this queue row. A listing can
   * have this alongside `pending_change`; the queue preserves the held-change
   * review workflow in that combined case.
   */
  manual_review_hold?: { trigger: string | null };
  // Task #5867 — the field-level diff of the open held change, so the
  // admin Pending Edits queue can render the actual edit inline
  // (each changed field's prior → proposed value + a photos-changed
  // indicator) without opening the listing detail. Best-effort on the
  // server: absent/null when there's no open held change or the payload
  // couldn't be read.
  pending_change_diff?: {
    proposalId?: number;
    proposalRevision?: string;
    fields: { field: string; prior: unknown; proposed: unknown; flag_categories?: string[] }[];
    images_changed: boolean;
    // Task #6230 — the newly proposed (held) photos resolved into the same
    // displayable image shape as `images`, so the queue can render thumbnails
    // and an enlarge/lightbox view inline. Absent/empty when there's no photo
    // change or the server couldn't resolve them (falls back to the
    // `images_changed` text indicator).
    proposed_images?: {
      id: number;
      url: string | null;
      r2_path: string | null;
      image: string | null;
      cdn_thumbnail: string | null;
      cdn_medium: string | null;
      cdn_full: string | null;
    }[];
  } | null;
}


export interface ModerationFlag {
  category: string;
  severity: "high" | "warning" | "info";
  detail: string;
  field?: string;
  imageId?: number;
  /**
   * Task #1706 — verbatim offending substring (text flags) or short
   * OCR/visual description (image flags), as returned by the moderation
   * AI. Surfaced in the admin Moderation Results panel as a quoted
   * block under each flag card; for text flags whose excerpt appears
   * verbatim in the source field, the panel also `<mark>`-highlights
   * the substring inline. Optional — older logs and non-AI gates
   * (pricing ceiling, year threshold) don't carry it.
   */
  excerpt?: string;
}


export interface ModerationLogRawResponse {
  source?: string;
  category?: string;
  warnings?: { field?: string; message?: string }[];
  [key: string]: unknown;
}


export type OwnerActivityKind =
  | "opened_editor"
  | "saved_draft"
  | "resubmitted"
  | "reminder_sent";


export interface OwnerActivityEntry {
  kind: OwnerActivityKind;
  at: string;
  actor?: string | null;
}


export interface ModerationLog {
  id: number;
  car_id: number;
  trigger: string;
  text_flags: ModerationFlag[];
  image_flags: ModerationFlag[];
  moderation_status: string;
  flagged_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewer_notes: string | null;
  created_at: string;
  raw_ai_response?: ModerationLogRawResponse | null;
  held_change?: unknown;
  is_current_content?: boolean;
  // Task #1645 — change-request tracking.
  owner_activity?: OwnerActivityEntry[] | null;
  responding_to_log_id?: number | null;
  original_reviewer_notes?: string | null;
}


// Task #1642 — unified moderation gate. The admin /admin/listings/:id
// endpoint computes one of these per blocking condition (AI flags,
// pending brand request, year threshold, pricing ceiling, US insurance
// review, non-US self-insurance attestation, US valuation, scan failure,
// or an unverified make with no pending request) so the moderation
// panel can render a single chip strip + per-gate row + the Approve
// confirm dialog without parsing raw moderation log shapes.
export type ModerationGateKind =
  | "ai_flags"
  | "pending_brand_request"
  | "year_threshold"
  | "pricing_ceiling"
  | "us_insurance_review"
  | "non_us_insurance_attestation"
  | "us_valuation"
  | "us_coverage_consent"
  | "scan_failure"
  // Task #1780 — durable-dispatcher placeholder states + the
  // synchronous pre-filter "hold" that survives across later AI
  // dispatches. Surfaced as gates so the moderation panel agrees
  // with the header pill on what's actually keeping the listing held.
  | "scan_in_progress"
  | "scan_dispatch_failed"
  | "pending_prefilter_hold"
  | "unverified_make";


export interface ModerationGate {
  kind: ModerationGateKind;
  label: string;
  detail: string;
  /** True if /moderate-approve will reject until the gate is cleared. */
  blocksApprove: boolean;
  /** Optional structured payload for inline UI (e.g. brand request id). */
  data?: Record<string, unknown>;
}


export interface StaleChangeRequestItem {
  car_id: number;
  owner_id: string;
  make: string | null;
  model: string | null;
  year: number | null;
  listing_status: string | null;
  requested_at: string;
  days_open: number;
  reviewer_notes: string | null;
  owner_activity: OwnerActivityEntry[];
  log_id: number;
}


export interface StaleChangeRequestsResponse {
  stale_days: number;
  items: StaleChangeRequestItem[];
}


export interface CarNote {
  id: number;
  note: string;
  created_at: string;
  created_by: string | null;
  author?: NoteAuthor | null;
}


export interface ListingDetail {
  pending_change_diff?: AdminListing["pending_change_diff"];
  vinMatches?: {
    status: "available" | "unavailable";
    matches: { id: number; listingStatus: string | null; state: "live" | "deleted" | "not_live"; simultaneouslyLive: boolean }[];
    truncated: boolean;
  };
  edit_proposal?: { proposalId: number; proposalRevision: string } | null;
  current_review?: {
    removed_photo_ids?: number[];
    revision: string;
    blockers: ModerationLog[];
    held_change_count: number;
    can_decide: boolean;
    decision_blocker: string | null;
    logs?: ModerationLog[];
    correction_decision_blocker?: string | null;
  };
  publicListingPath?: string | null;
  publicHostPath?: string | null;
  publicHostStatus?: "direct" | "restored" | "unavailable" | "held" | "retired";
  publicHostSurvivorId?: number | null;
  readiness?: import("@workspace/listing-requirements").ListingReadiness;
  correction_approval_blockers?: string[];
  change_request?: { id: number; notes: string | null; requestedAt: string; revision: string; awaitingOwner?: boolean } | null;
  id: number;
  lifecycle?: CarLifecycle | null;
  make: string;
  model: string;
  year: number;
  sub_model?: string | null;
  vin?: string | null;
  engine?: string | null;
  listing_status: string;
  approved_at?: string | null;
  description: string | null;
  // Dual-column normalisation: the API coalesces `cars.car_history` with
  // `cars.history` before returning this field, so it is always populated
  // for listings whose content was written by the wizard or listings-sanitiser
  // (which canonicalise to `history`). See admin/listings/core.ts response.
  car_history?: string | null;
  pickup_instructions?: string | null;
  internal_notes?: string | null;
  country_code?: string | null;
  insured_value?: number | null;
  insurance_exempt_liability?: boolean;
  insurance_exempt_physical?: boolean;
  // Task #1635. Non-US self-insurance attestation. Snapshot of the
  // attestation captured atomically when the host clicks "I confirm" in
  // the wizard / edit modal. The country snapshot is what the submit
  // gate compares against the live country_code to detect a stale
  // attestation. NULL on US listings (gate never asks) and on listings
  // that haven't been attested yet.
  self_insurance_attestation_at?: string | null;
  self_insurance_attestation_by?: number | null;
  self_insurance_attestation_country_code?: string | null;
  self_insurance_attestation_user?: { first_name: string | null; last_name: string | null; email: string | null } | null;
  price_per_day: number | null;
  price_driving?: number | null;
  price_event?: number | null;
  price_chauffeured?: number | null;
  currency_code?: string | null;
  transmission: string | null;
  fuel_type: string | null;
  brakes?: string | null;
  wheels?: string | null;
  mileage?: number | null;
  classification?: string | null;
  fuel_policy?: string | null;
  weather_policy?: string | null;
  score_override?: number | null;
  owner_share?: number | null;
  deposit?: number | null;
  city: string | null;
  state: string | null;
  created_at: string;
  is_active?: boolean;
  featured?: boolean;
  deleted_by_owner?: boolean;
  disabled_by_owner?: boolean;
  awaiting_owners_response?: boolean;
  rental_driving_active?: boolean;
  rental_chauffeured_active?: boolean;
  rental_event_active?: boolean;
  chauffeured_hourly_rate?: number | null;
  event_hourly_rate?: number | null;
  minimum_price?: number | null;
  driving_rental_min_duration?: number | null;
  chauffeured_min_price_hourly_rate?: number | null;
  event_min_price_hourly_rate?: number | null;
  mileage_limit?: number | null;
  mileage_limit_per_day?: number | null;
  overage_rate_per_mile?: number | null;
  mileage_limit_miles?: number | null;
  mileage_overage_rate?: number | null;
  delivery_enabled?: boolean;
  delivery_free?: boolean;
  delivery_fee_per_unit?: number | null;
  delivery_fee_min?: number | null;
  delivery_radius_free?: number | null;
  delivery_radius_full?: number | null;
  owner: { id: string; first_name: string; last_name: string; email: string; phone: string | null } | null;
  images: { id: number; url: string | null; r2_path: string | null; image: string | null; cdn_thumbnail: string | null; cdn_medium: string | null; cdn_full: string | null; is_primary: boolean; display_order: number; is_active: boolean }[];
  held_images?: ListingDetail["images"];
  removed_images?: ListingDetail["images"];
  moderation_logs?: ModerationLog[];
  // Task #1645 — older `changes_requested` logs referenced by
  // `responding_to_log_id` from the latest-10 window are hydrated here
  // by /admin/listings/:id so the UI can always render the original
  // reviewer identity/date alongside carried-forward notes.
  moderation_logs_linked?: ModerationLog[];
  // Task #1547 follow-up — when the listing is blocked on a host-
  // submitted unverified make, the API hydrates the request row plus
  // fuzzy-matched suggestions against the verified-brand registry so
  // the moderation panel can offer one-click "approve as <existing
  // brand>" alongside "approve as new" and "reject".
  pending_make_request?: {
    id: number;
    original_text: string;
    normalised_name: string | null;
    status: "pending" | "approved" | "rejected";
    created_at: string;
    decided_at: string | null;
    decision_note: string | null;
    suggestions: Array<{
      id: number;
      canonical_name: string;
      distance: number;
      similarity: number;
      reason: "exact" | "prefix" | "contains" | "fuzzy";
    }>;
  } | null;
  // Task #1642 — unified moderation gates payload. One row per blocking
  // condition (AI flags, pending brand request, year threshold, pricing
  // ceiling, US insurance review, missing self-insurance attestation,
  // valuation, scan failure). Source of truth for the admin moderation
  // panel's gate chips and the Approve confirm dialog.
  moderation_gates?: ModerationGate[];
  // Task #2499 — when the listing is parked in `awaiting_approval`
  // *and* the latest moderation log is `clean`, this carries the same
  // hold-reason set the runner uses to drive the #moderation Slack
  // alert (derived from `loadListingModerationGates`). The detail
  // header renders one pill per reason under the AWAITING APPROVAL
  // badge so admins see exactly *why* a clean scan didn't auto-activate.
  // Empty / undefined when the listing isn't in that specific state.
  held_clean_reasons?: { kind: string; label: string; detail?: string }[];
  // Task #6386 follow-on — an OPEN manual-review hold (held_change IS NULL,
  // e.g. the pricing guardrail) on a listing that is PAST awaiting_approval
  // (typically already active). These holds fit neither admin queue, so the
  // detail panel uses this to offer a "resolve hold" approve action.
  open_manual_review_hold?: { trigger: string | null; created_at: string | null } | null;
  notes?: CarNote[];
  scores?: { score_overall: number | null; review_count: number | null } | null;
  [key: string]: unknown;
}


export interface TimelineEvent {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  actor?: string;
  metadata?: Record<string, unknown>;
}


export interface AdminUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  platform_origin: string | null;
  is_active: boolean;
  renter_verified: boolean;
  admin_role: number;
  created_at: string;
  last_login_at: string | null;
  profile_image: string | null;
  host_profile_moderation_status: string | null;
  // Task #6333 — attached only when the list is loaded with
  // filter=pending_profile_moderation: summary of the row's OPEN moderation
  // logs so the queue can show per-row context and distinguish actionable
  // rows from "awaiting host revision" ones.
  moderation_summary?: {
    has_open_log: boolean;
    open_log_count: number;
    reasons: string[];
    flagged_fields: string[];
  } | null;
  // Task #6809 — true only when the list is loaded with filter=auto_paused.
  // The host's listings are currently offline because a booking request
  // expired without a response (latest audit_log marker is
  // expiry_auto_pause with no subsequent expiry_auto_unpause).
  is_auto_paused?: boolean;
}


export interface UserDetailCar {
  id: number;
  make: string;
  model: string;
  year: number;
  listing_status: string;
  created_at: string;
  images?: Array<{
    url: string | null;
    image: string | null;
    cdn_thumbnail: string | null;
    cdn_medium: string | null;
    is_primary: boolean | null;
  }> | null;
}


export interface UserDetailTripParty {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export interface UserDetailTrip {
  id: number;
  public_id: string;
  status: string;
  pick_up: string | null;
  started_at: string;
  drop_off: string;
  created_at: string;
  car: { make: string; model: string; year: number; primary_image?: unknown } | null;
  // Optional CS-enrichment fields (Task #5089). Older payloads omit these, so
  // every consumer must degrade gracefully when they are absent.
  times_captured?: boolean | null;
  renter_id?: number | null;
  owner_id?: number | null;
  renter?: UserDetailTripParty | null;
  owner?: UserDetailTripParty | null;
  payment_status?: number | string | null;
  payment_due?: number | string | null;
  rental_fee?: number | string | null;
  service_fee?: number | string | null;
  owner_earn?: number | string | null;
  local_currency_code?: string | null;
  is_delivery?: boolean | number | null;
  delivery_address?: string | null;
  airport_code?: string | null;
  payout_status?: number | null;
  total_price?: number | string | null;
}


export interface NoteAuthor {
  id: number;
  first_name: string | null;
  last_name: string | null;
}


export interface UserNote {
  id: number;
  note: string;
  created_at: string;
  created_by: string | null;
  author: NoteAuthor | null;
}


export interface KycEntry {
  id: number;
  type: string;
  status: string;
  created_at: string;
}


export interface UserBillingEntry {
  id: number;
  trip_id: number | null;
  /** Joined trip UUID so the admin user-detail billing tab can show the
   *  customer-facing short ref instead of the internal numeric id
   *  (task #2388). */
  trip_public_id?: string | null;
  operation: number;
  amount: number;
  currency?: string;
  is_succeeded: boolean;
  created_at: string;
}


export interface UserDetailUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  country_code: string | null;
  platform_origin: string | null;
  is_active: boolean;
  renter_verified: boolean;
  admin_role: number;
  created_at: string;
  last_login_at: string | null;
  profile_image: string | null;
  stripe_customer_id_us: string | null;
  stripe_customer_id_intl: string | null;
  host_bio: string | null;
  car_story: string | null;
  auto_experience: string | null;
  host_profile_moderation_status: string | null;
  notification_preferences: Record<string, unknown> | null;
  [key: string]: unknown;
}


export interface UserDriverInfo {
  id: number;
  license_verification_status: string | null;
  is_active: boolean;
  created_at: string;
}


export interface UserDetailData {
  user: UserDetailUser;
  cars: UserDetailCar[];
  trips: UserDetailTrip[];
  /**
   * Task #7387 — total trip count for this user (both renter and owner).
   * Used by the admin user profile to know whether a "Load more" button
   * should be shown. Optional so older API builds are tolerated.
   */
  trips_total?: number;
  notes: UserNote[];
  kyc: KycEntry[];
  billing: UserBillingEntry[];
  /**
   * Total billing-row count for this user, mirroring `trips_total`. Used by
   * the admin user profile to know whether a billing "Load more" button
   * should be shown. Optional so older API builds are tolerated.
   */
  billing_total?: number;
  driver: UserDriverInfo | null;
}


export interface AdminTrip {
  id: number;
  public_id: string;
  status: string;
  started_at: string;
  pick_up: string;
  drop_off: string;
  created_at: string;
  booking_type: string | null;
  // Source provenance for the "Imported" pill (Task #5393). These mirror the
  // shared `isImportedTrip` helper's inputs so legacy CSV imports, bulk-migrated
  // DriveShare rows, and native-historic DriveShare rows are identifiable.
  source: string | null;
  source_id: number | string | null;
  platform_origin: string | null;
  // Rental fee and currency remain available for existing callers; the Trips
  // table's separate total fields below are the full renter commitment.
  rental_fee: number | null;
  local_currency_code: string | null;
  resolved_price?: number;
  resolved_currency?: string | null;
  /** Full renter commitment from the current quote or verified legacy total. */
  resolved_total_price?: number | null;
  resolved_total_currency?: string | null;
  // Task #5500 — warm-pipeline (DRAFT) markers. Present only on rows that are
  // NOT real trips: unpaid host quotes + abandoned checkouts surfaced in the
  // DRAFT booking-funnel bucket. Trip-only row actions are disabled for these.
  is_pipeline?: boolean;
  pipeline_kind?: "quote" | "checkout";
  // Task #6079 — warm-vs-lapsed state for the Drafts status chip: checkouts
  // are "live" or "abandoned" (failed / past expiry), quotes are
  // "awaiting_payment" or "expired".
  pipeline_state?: "live" | "abandoned" | "awaiting_payment" | "expired";
  pipeline_ref?: string;
  /** Persisted creation time of the quote/session; canonical Draft ordering date. */
  attempted_at?: string;
  expires_at?: string | null;
  age_days?: number;
  rental_type?: string | null;
  times_captured?: boolean | null;
  car: {
    id: number;
    make: string;
    model: string;
    year: number;
    country_code?: string | null;
    currency_code?: string | null;
    primary_image?: unknown;
  } | null;
  renter: { id: string; first_name: string; last_name: string; email: string } | null;
  owner: { id: string; first_name: string; last_name: string; email: string } | null;
}


export type TripPricingSource = "snapshot" | "trip" | "none";


export interface TripPricing {
  source: TripPricingSource;
  rental_fee: number;
  service_fee: number;
  insurance_fee: number;
  delivery_fee: number;
  booking_fee: number;
  deposit: number;
  payment_due: number;
  owner_earn: number;
  currency: string;
  insurance_package_name: string | null;
  refundable_surcharge: number | null;
  refundable_surcharge_percent: number | null;
  payment_type: string | null;
  platform_payment_due: number | null;
  payment_plan: string | null;
  second_charge_amount: number | null;
  second_charge_due_at: string | null;
  payment_timing: string | null;
  security_deposit_status: number | null;
  payout_status: number | null;
  mileage_limit: number | null;
  mileage_limit_miles: number | null;
  mileage_overage_rate: number | null;
  snapshot_id: number | null;
  snapshot_status: string | null;
  /**
   * Task #8163 — applied discounts persisted at booking time (snapshot
   * source only). Null on historical rows ⇒ no discount rows rendered.
   * Optional for older server builds.
   */
  coupon_amount?: number | null;
  coupon_code?: string | null;
  pro_owner_discount?: number | null;
  pro_owner_tier?: string | null;
}


export interface TripFraudReview {
  active: boolean;
  score: number | null;
  flags: Array<{ rule: string; detail: string; weight: number }>;
  flagged_at: string | null;
  public_id: string | null;
}


/** Raw `inspection_images` row embedded on a trip-detail inspection. */
export interface TripInspectionPhoto {
  id: number;
  image: string | null;
  link: string | null;
  comment: string | null;
}


/**
 * Damage marker captured on the handover's car silhouette (mobile
 * `DamageMarker`): normalised 0..1 coordinates plus the host's severity
 * selection. Stored as jsonb so every field is treated as optional.
 */
export interface TripDamageMarker {
  x?: number;
  y?: number;
  label?: string | null;
  severity?: "minor" | "moderate" | "severe" | string | null;
}


/**
 * Handover inspection row as returned (raw) by GET /admin/trips/:id
 * (tasks #2520 / migration 3013). `type` is "pre_trip" (check-in) or
 * "post_trip" (check-out); legacy rows use "0" (pickup) / "1" (dropoff).
 * Signature fields hold either a serialized SVG path (multi-`M`/`L`
 * `d` attribute from the mobile SignaturePad) or an object-storage
 * path/URL. Fuel levels are integer eighths 0..8.
 */
export interface TripInspection {
  id: number;
  trip_id: number;
  type: string | null;
  status: string | null;
  created_at: string;
  user_id: number | null;
  mileage_start: number | null;
  mileage_end: number | null;
  fuel_level_start: number | null;
  fuel_level_end: number | null;
  check_in_signature: string | null;
  check_out_signature: string | null;
  host_signature_path: string | null;
  skip_reason: string | null;
  damage_markers: TripDamageMarker[] | unknown;
  notes: string | null;
  is_active: boolean | null;
  photos: TripInspectionPhoto[];
}


/**
 * Task #8231 — post-trip mileage-overage settlement record.
 * Null when the trip has no mileage cap or no settlement has been created yet.
 * Optional so the UI tolerates older API builds that don't emit the field.
 */
export interface MileageOverageSettlement {
  id: number;
  trip_id: number;
  /** Settlement lifecycle: pending/processing → succeeded / failed / ineligible. */
  status: string;
  /** When status === "ineligible": human-readable reason for exclusion. */
  ineligible_reason: string | null;
  /** Country gate captured when the settlement was first evaluated. */
  country_code: string | null;
  /** Booking rental type used by the self-drive gate. */
  rental_type: string | null;
  /** Daily allowance frozen at booking time (miles). */
  frozen_allowance_per_day: number | null;
  /** Per-mile overage rate frozen at booking time (USD). */
  frozen_rate_per_mile: number | null;
  /** Odometer reading from the pre-trip (check-in) inspection. */
  pre_trip_mileage: number | null;
  /** Odometer reading from the post-trip (check-out) inspection. */
  post_trip_mileage: number | null;
  /** Number of rental days used in the included-miles calculation. */
  final_charged_days: number | null;
  /** Total miles driven (post_trip_mileage − pre_trip_mileage). */
  driven_miles: number | null;
  /** Miles covered by the daily allowance (frozen_allowance_per_day × final_charged_days). */
  included_miles: number | null;
  /** Miles that exceeded the allowance (driven_miles − included_miles, floored at 0). */
  overage_miles: number | null;
  /** Renter-facing mileage charge (overage_miles × frozen_rate_per_mile). */
  mileage_amount: number | null;
  /** Platform admin fee on top of the mileage charge. */
  admin_fee: number | null;
  /** Total charged to the renter (mileage_amount + admin_fee). */
  total_charge: number | null;
  /** Mileage component paid to the host; excludes the $25 platform fee. */
  host_share_amount: number | null;
  /** Stripe payment status for the renter charge (e.g. "succeeded", "failed"). */
  payment_status: string | null;
  /** Stripe PaymentIntent id for the renter charge. */
  payment_intent_id: string | null;
  /** Stripe region key ("us" | "intl") — used to build dashboard deep links. */
  stripe_region: string | null;
  /** Stripe failure message when the charge was declined / errored. */
  failure_reason: string | null;
  /** True when the settlement needs human admin review; no automatic retry occurs. */
  admin_review_required: boolean;
  /** Host-share state: not_due, pending_payout, or included_in_payout. */
  host_share_status: string;
  /** ISO timestamp when the renter was charged. */
  charged_at: string | null;
  created_at: string;
  updated_at: string;
}


export interface TripDetailData {
  trip: Record<string, unknown> & {
    public_id: string;
    status: string;
    started_at: string;
    drop_off: string;
    under_fraud_review?: boolean | null;
    /** Cancellation context surfaced for cancelled / declined bookings. */
    cancelled_reason_id?: number | string | null;
    cancelled_details?: string | null;
    cancelled_at?: string | null;
    /**
     * Human-readable cancellation reason name resolved server-side from
     * `cancellation_reasons` for `cancelled_reason_id`. Null when the reason
     * row is missing / inactive (UI falls back to `Reason #N`).
     */
    cancellation_reason_name?: string | null;
    car?: { make: string; model: string; year: number; city?: string; state?: string } | null;
    renter?: { first_name: string; last_name: string; email: string } | null;
    owner?: { first_name: string; last_name: string; email: string } | null;
    /** Task #8211 — scoped driver context; null when the server build failed. */
    driver_context?: AdminBookingDriverContext | null;
    /** Moderator-only; null means the status lookup failed. Never contains provider links. */
    unbound_verification_starts?: Array<{
      context_id: number;
      driver_id: number;
      assignment_id: number | null;
      subject: string;
      claimed_at: string;
      binding_state: "unbound";
    }> | null;
  };
  suggestion: Record<string, unknown>[];
  billing: BillingRecord[];
  /** Classified historical settlement evidence; unavailable after a failed lookup. */
  historical_settlement?: HistoricalSettlement;
  imported_money?: ImportedMoneyContext;
  notes: TripNote[];
  /**
   * Handover inspections (both phases) for the trip — self-drive
   * check-in / check-out audit data. Optional for older server builds.
   */
  inspections?: TripInspection[];
  /**
   * Task #999: server-resolved active price snapshot (latest accepted
   * trip_suggestion → latest in-flight → trip-row fallback). Always present;
   * `source === "none"` means no pricing data exists yet.
   */
  pricing: TripPricing;
  /** Authoritative Shelby voice-decision attribution, if any. */
  confirmation_attribution?: {
    source: "shelby_call" | null;
    outcome: "accepted" | "declined" | null;
    reason: string | null;
    attemptId: number | null;
  } | null;
  /** Task #2728 — fraud-review banner context (score, flags, timestamp). */
  fraud_review?: TripFraudReview | null;
  /**
   * Task #8231 — post-trip mileage-overage settlement record. Null when the
   * trip has no mileage cap or no settlement has been created yet. Optional so
   * the UI tolerates older API builds that don't emit the field.
   */
  mileage_overage_settlement?: MileageOverageSettlement | null;
}


export interface HistoricalSettlement {
  available: boolean;
  movements: Array<{
    kind: "rental_payment" | "insurance_payment" | "rental_refund" |
      "insurance_refund" | "deposit_hold" | "deposit_charge" | "deposit_return" | "host_payout";
    amount: number;
    currency: string;
    status: "succeeded";
    at: string | null;
    provenance: "driveshare_billing" | "platform_billing" | "recorded_external_payout";
    billing_id: number;
    /** Recorded movement classification, never full-settlement authority. */
    transfer_kind?: "manual_adjusted";
  }>;
}

export interface ImportedMoneyContext {
  mode: "bac" | "driveshare" | "unknown";
  payout: "recorded" | "auto_stamped" | "missing" | "unavailable";
  historical: boolean;
  settlement: HistoricalSettlement;
}

export interface BillingRecord {
  id: number;
  operation: number;
  amount: number;
  currency_code?: string | null;
  is_succeeded: boolean;
  is_done: boolean;
  stripe_object_id: string | null;
  stripe_region?: string | null;
  type?: number | null;
  /** Task #7405 — true when this row is an adjustment-delta charge (price
   *  increase accepted via a change request). Stamped server-side from
   *  `billing.type === 3` or a trip_suggestions pending_payment_intent_id match
   *  for legacy rows. Used by deriveBookingMoneyGlance to surface the split. */
  is_adjustment?: boolean;
  created_at: string;
}


export interface RefundComponentBreakdown {
  rentalFee: number;
  bookingFee: number;
  deliveryFee: number;
  insuranceFee: number;
  serviceFee: number;
}


export interface RefundPreviewResult {
  previewId: string;
  balanceMinor: number;
  minorUnitFactor: number;
  expiresAt: string;
  existingAttempt?: { previewId: string; intent: "full" | "partial"; amount: number; currency: string; reason: string } | null;
  snapshot: {
    version: 1;
    rate_plan: "flexible" | "non_refundable";
    owner_cancellation_refund_pct: number;
    renter_cancellation_tiers: { min_hours_before_pickup: number; refund_pct: number }[];
  };
  inputComponents: RefundComponentBreakdown;
  baseComponents: RefundComponentBreakdown;
  netCaptured: number;
  evaluation: {
    refundAmount: number;
    refundPct: number;
    reason: string;
    policy: string;
    components: RefundComponentBreakdown;
    retained: RefundComponentBreakdown;
  };
  /**
   * Task #8482 — the currency the trip's captured funds are actually in
   * (earliest succeeded captured billing-ledger row's currency, falling back
   * to the billing-first trip-currency resolver, then local, then USD).
   * Every row the Refund / cancellation calculator renders — plus the
   * post-refund confirmation toast — must format in THIS currency, not the
   * page-level local/market pricing currency, because Stripe already
   * refunds in the original charge's currency.
   */
  settlementCurrency: string;
}


export interface TripExtension {
  id: number;
  status: "pending" | "accepted" | "declined" | "superseded" | "expired";
  start_date: string;
  end_date: string;
  rental_fee: number;
  service_fee: number;
  insurance_fee: number;
  delivery_fee: number;
  deposit: number;
  payment_due: number;
  owner_earn: number;
  initiator_id: string;
  initiator_role: "host" | "renter";
  responder_id: string;
  expires_at: string | null;
  expired_at: string | null;
  created_at: string;
}


export interface TripExtensionResponse {
  pending: TripExtension | null;
  history: TripExtension[];
}


export interface TripNote {
  id: number;
  note: string;
  created_at: string;
  created_by: string | null;
  author: NoteAuthor | null;
}


export type ReviewFlagSeverity = "high" | "warning" | "info";


export type ReviewFlagCategory =
  | "personal_attack"
  | "off_topic"
  | "refund_threat"
  | "off_platform_contact"
  | "negative_sentiment";


export interface ReviewFlag {
  category: ReviewFlagCategory;
  severity: ReviewFlagSeverity;
  detail: string;
  match?: string;
}


export type ReviewerTrustLevel = "trusted" | "established" | "new" | "watchlist";


export interface ReviewerTrustBadge {
  level: ReviewerTrustLevel;
  score: number;
  reasons: string[];
}


export interface ReviewRiskBadge {
  score: number;
  level: "low" | "medium" | "high";
  signals: { label: string; weight: number }[];
}


export interface AdminReview {
  id: number;
  publicId: string | null;
  rating: number | null;
  reviewText: string | null;
  commentModerated: string | null;
  moderated: boolean;
  isActive: boolean;
  createdAt: string;
  tripId: number | null;
  carId: number | null;
  reviewerId: string | null;
  reviewer: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    profileImage: string | null;
    createdAt: string | null;
  } | null;
  reviewee: { id: string; first_name: string | null; last_name: string | null } | null;
  trip: {
    id: number;
    public_id: string | null;
    status: string | null;
    pick_up: string | null;
    drop_off: string | null;
    started_at: string | null;
    completed_at: string | null;
    price_billing_total: number | null;
  } | null;
  car: {
    id: number;
    publicId: string | null;
    make: string | null;
    model: string | null;
    year: number | null;
    ownerId: string | null;
  } | null;
  hostName: string | null;
  aiFlags: ReviewFlag[];
  trust: ReviewerTrustBadge;
  risk: ReviewRiskBadge;
}


export interface FlaggedMessage {
  id: number;
  content: string;
  is_filtered: boolean;
  is_hidden?: boolean;
  flag_categories?: string[];
  flag_reason?: string | null;
  created_at: string;
  // Task #6615 — image attachments surface in moderation queues.
  attachment_url?: string | null;
  attachment_type?: string | null;
  sender: { id: string; first_name: string; last_name: string; email: string } | null;
  conversation: { id: number; public_id: string } | null;
}


// Task #6615 — held chat photo pending safety review.
export interface HeldPhotoRow {
  id: number;
  conversation_id: number | null;
  conversation_public_id: string | null;
  sender: FlaggedConversationUser | null;
  recipient_id: number | null;
  flag_categories: string[];
  flag_reason: string | null;
  attachment_url: string | null;
  created_at: string;
}


export interface HeldPhotoListResponse {
  photos: HeldPhotoRow[];
  total: number;
  page: number;
  limit: number;
  capped: boolean;
}


export interface FlaggedConversationUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  profile_image?: string | null;
  is_active?: boolean;
}


export interface FlaggedConversationCar {
  id: number;
  make: string | null;
  model: string | null;
  year: number | null;
  slug?: string | null;
}


export interface FlaggedConversationLastAction {
  action: string;
  created_at: string;
  performed_by: string;
}


export interface FlaggedConversationSummary {
  conversation_id: number;
  public_id: string | null;
  car: FlaggedConversationCar | null;
  // Task #4207 — raw participant ids alongside the (possibly null) resolved
  // user objects, so the list can distinguish an unimported/migrated
  // participant (id present, user row absent → "Former guest") from a genuinely
  // absent participant, and never render a bare "Unknown".
  user_1_id: string | null;
  user_2_id: string | null;
  user_1: FlaggedConversationUser | null;
  user_2: FlaggedConversationUser | null;
  flag_count: number;
  // Task #7636 — unresolved flagged messages with is_hidden=true; 0 means every
  // pending flag was still delivered (amber queue styling rather than rose).
  // Optional so responses from older API versions degrade gracefully.
  hidden_count?: number;
  last_flagged_at: string;
  latest_flag_id: number;
  latest_content: string;
  latest_reason: string | null;
  categories: string[];
  last_action: FlaggedConversationLastAction | null;
  flagged_message_ids: number[];
  // Task #6146 — market payment context for triaging payment flags: in
  // partial-pay markets the host legitimately collects the rental balance
  // directly, so payment mentions can be fine there. "unknown" when there is
  // no linked car / resolvable country. Optional so cached payloads render.
  market_country_code?: string | null;
  market_payment_type?: MarketPaymentType;
}


// Task #6146 — derived from the car's country via the same platform_config
// payment_type source the moderation classifier uses.
export type MarketPaymentType = "partial" | "full" | "unknown";


export interface FlaggedConversationListResponse {
  conversations: FlaggedConversationSummary[];
  total: number;
  page: number;
  limit: number;
  view: string;
}


export interface ConversationThreadMessage {
  id: number;
  // Task #5939 — flagged bodies are delivered to admins directly (no reveal
  // step); `content` carries the real body. User-facing `is_hidden` suppression
  // for the other participant is unchanged. (`content` may still be null for
  // genuinely empty/system rows.)
  content: string | null;
  is_filtered: boolean;
  is_hidden: boolean;
  is_system: boolean;
  // Task #6656 — lets the admin thread render automated rows (e.g. the
  // moderation block notice) as clearly system-authored instead of
  // misattributing them to the participant whose send was blocked.
  system_type?: string | null;
  flag_categories: string[];
  flag_reason: string | null;
  redacted?: boolean;
  sender_id: string | null;
  // Task #6105 — present on offender-detail flagged messages so the drilldown
  // can cross-link each flag to its Messages-queue conversation.
  conversation_id?: number | null;
  created_at: string;
  // Task #6615 — image attachment rows render a blurred, audit-revealed
  // thumbnail in the admin thread view.
  attachment_url?: string | null;
  attachment_type?: string | null;
}


export interface MessageModerationActionRecord {
  id: number;
  message_id: number;
  action: string;
  note: string | null;
  template_slug: string | null;
  performed_by: string;
  // Task #6105 — acting admin's display identity, hydrated server-side so the
  // "Recent actions" audit never shows a bare uuid.
  performed_by_admin?: { id: number; first_name: string | null; last_name: string | null; email: string | null } | null;
  created_at: string;
}


// Task #5975 — moderation-friendly booking status between the two parties.
export type BookingStatusCategory = "positive" | "pending" | "none";

export interface ConversationBookingStatus {
  /** Raw `trips.status`, or null when there is no booking. */
  status: string | null;
  /** Plain-language label (e.g. "Request", "Confirmed", "No booking yet"). */
  label: string;
  category: BookingStatusCategory;
  trip_id: number | null;
  public_id: string | null;
}

export interface FlaggedConversationDetailResponse {
  conversation: {
    id: number;
    public_id: string;
    car_id: number | null;
    user_1_id: string;
    user_2_id: string;
    trip_id: number | null;
    last_message_at: string | null;
    created_at: string;
    user_1: FlaggedConversationUser | null;
    user_2: FlaggedConversationUser | null;
    // Enforcement summary per party (suspended/banned + why + pinned notes)
    // so reviewers see how each participant is currently being treated.
    user_1_enforcement?: UserEnforcementSummary | null;
    user_2_enforcement?: UserEnforcementSummary | null;
    car: FlaggedConversationCar | null;
    // Task #5975 — booking status between the two parties for this car.
    booking: ConversationBookingStatus;
    // Task #6146 — market payment context for the car under discussion.
    market_country_code?: string | null;
    market_payment_type?: MarketPaymentType;
  };
  messages: ConversationThreadMessage[];
  actions: MessageModerationActionRecord[];
  /** Authoritative conversation-scoped cycle derived from complete action history. */
  senior_review?: {
    pending: boolean;
    latest_escalation: MessageModerationActionRecord | null;
    pending_message_ids: number[];
  };
  // Task #6105 — per-participant enforcement context for the side panel,
  // keyed by String(user id). Optional so older cached payloads still render.
  participant_enforcement?: Record<string, ParticipantEnforcementSummary>;
}


// Task #6105 — per-participant enforcement summary in the thread side panel.
export interface ParticipantEnforcementSummary {
  user_id: number;
  state: EnforcementState;
  warn_count: number;
  flag_count: number;
  conversation_count: number;
  last_enforcement_at: string | null;
}


export interface MessagePatternSample {
  conversation_id: number;
  public_id: string | null;
  sample_message_id: number;
  preview: string;
  sender_id: string | null;
  created_at: string;
}


export interface MessagePatternResponse {
  phrase: string;
  total: number;
  /** Lookback window the count is computed over (default 7 days). */
  window_days: number;
  conversations: MessagePatternSample[];
}


// Task #5405 — Repeat Offender Enforcement Console.
export type EnforcementState = "clean" | "flagged" | "warned" | "suspended" | "banned";


export interface ChatOffenderRow {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  flag_count: number;
  score: number;
  categories: string[];
  top_category: string | null;
  first_flagged_at: string | null;
  last_flagged_at: string | null;
  warn_count: number;
  last_warn_at: string | null;
  reoffended_after_warn: boolean;
  role: "renter" | "host";
  is_active: boolean;
  is_blacklisted: boolean;
  enforcement_state: EnforcementState;
  needs_action: boolean;
  // Task #6105 — distinct flagged conversations in the same window.
  conversation_count: number;
}


export interface ChatOffenderEnforcementEvent {
  kind: "warn" | "suspend" | "unsuspend" | "ban";
  action: string;
  created_at: string;
  note: string | null;
  message_id: number | null;
  admin: { id: number; first_name: string | null; last_name: string | null; email: string | null } | null;
}


export interface ChatOffenderDetailResponse {
  user: {
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    profile_image: string | null;
    role: "renter" | "host";
    is_active: boolean;
    is_blacklisted: boolean;
    enforcement_state: EnforcementState;
    // Task #6105 — full (uncapped) flag / distinct-conversation counts.
    flag_count: number;
    conversation_count: number;
  };
  messages: ConversationThreadMessage[];
  history: ChatOffenderEnforcementEvent[];
}


// Shared user-enforcement visibility — suspend status that follows the user
// onto every admin surface where CS acts on them (moderation party cards,
// User 360, conversation console). Mirrors api-server lib/user-enforcement.ts.
export interface UserEnforcementAdminRef {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}


export interface UserEnforcementPinnedNote {
  id: number;
  note: string;
  created_at: string | null;
  pinned_at: string | null;
  admin: UserEnforcementAdminRef | null;
}


export interface UserEnforcementSummary {
  user_id: number;
  is_active: boolean;
  is_blacklisted: boolean;
  /** banned > suspended > active (warn state needs the detail endpoint). */
  state: "banned" | "suspended" | "active";
  suspended_at: string | null;
  suspended_reason: string | null;
  suspended_by: UserEnforcementAdminRef | null;
  /** Up to 3 most recently pinned internal notes — they follow the user. */
  pinned_notes: UserEnforcementPinnedNote[];
}


export interface UserEnforcementResponse {
  summary: UserEnforcementSummary | null;
  warn_count: number;
  enforcement_state: Exclude<EnforcementState, "clean">;
  is_blacklisted: boolean;
  history: ChatOffenderEnforcementEvent[];
}


export interface PlatformConfig {
  id: number;
  key: string;
  value: string | null;
  value_json: Record<string, unknown> | null;
  market: string | null;
  environment: string | null;
  description: string | null;
}


export interface AuditLogEntry {
  id: number;
  entity_type: string;
  entity_id: string | null;
  action: string;
  changed_by: string | null;
  changes: Record<string, unknown> | null;
  created_at: string;
  impersonation_session_id?: string | null;
}


export interface ImpersonationSessionUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}


export interface ImpersonationSessionEntry {
  id: string;
  admin_user_id: string;
  target_user_id: string;
  reason: string;
  ip_address: string | null;
  user_agent: string | null;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  end_reason: string | null;
  admin: ImpersonationSessionUser | null;
  target: ImpersonationSessionUser | null;
  action_count: number;
}


export interface ConversionTrend {
  direction: "up" | "down" | "neutral";
  label: string;
  current: number;
  previous: number;
  changePct: number;
  series: { month: string; rate: number; total: number; completed: number }[];
}


export interface AnalyticsPeriod {
  from: string;
  to: string;
  compareFrom?: string;
  compareTo?: string;
  preset: string;
}

export interface AnalyticsBusinessOverviewMonth {
  month: number;
  label: string;
  values: Record<string, number | null>;
}

export interface AnalyticsBusinessOverviewComparison {
  currentYear: number;
  current: number;
  previous: number;
  absoluteChange: number;
  percentageChange: number | null;
  cutoff: string;
  priorCutoff: string;
}

export interface AnalyticsBusinessOverview {
  years: number[];
  monthlyBookings: AnalyticsBusinessOverviewMonth[] | null;
  monthlyPlatformRevenue: AnalyticsBusinessOverviewMonth[] | null;
  bookingsYtd: AnalyticsBusinessOverviewComparison | null;
  platformRevenueYtd: AnalyticsBusinessOverviewComparison | null;
  countries: Array<{
    code: string;
    label: string;
    current: number;
    previous: number;
    absoluteChange: number;
    percentageChange: number | null;
  }> | null;
  availability: {
    monthlyBookings: boolean;
    monthlyPlatformRevenue: boolean;
    priorityCountries: boolean;
  };
  period: {
    generatedAt: string;
    historyFrom: string;
    historyTo: string;
    currentYear: number;
    currentMonth: number;
    currentYtdFrom: string;
    currentYtdTo: string;
    priorYtdFrom: string;
    priorYtdTo: string;
    rollingFrom: string;
    rollingTo: string;
    priorRollingFrom: string;
    priorRollingTo: string;
  };
  partialFailures: Array<{ source: string; error: string }>;
}


export interface AnalyticsData {
  /** Period info — optional for compat with older API builds during deploys. */
  period?: AnalyticsPeriod;

  /**
   * Task #8163 — applied booking discounts (Pro Owner + coupon) in period.
   * Revenue series are built from actual charges → NET of these discounts;
   * gross = net + discounts.total. Optional for older API builds.
   */
  discounts?: {
    proOwnerTotal: number;
    couponTotal: number;
    total: number;
    discountedBookings: number;
    revenueBasis: string;
    /**
     * Authoritative payment_due-based figures for the charged (op=1)
     * population: net = committed renter totals (incl. pay-host-directly on
     * partial-pay), gross = net + applied discounts. Optional for older
     * API builds.
     */
    chargedTrips?: number;
    netRevenue?: number;
    grossRevenue?: number;
  };

  // ── Existing fields (now period-scoped) ────────────────────────────────
  revenueByMonth: { month: string; amount: number; platformTake?: number; ownerPortion?: number }[];
  revenueByMonthComparison?: { month: string; amount: number; platformTake?: number; ownerPortion?: number }[];
  /** Sub-query failures — individual metrics may be zero but the page still loads */
  partialFailures?: Array<{ source: string; error: string }>;
  bookingsByType: { type: string; count: number }[];
  conversionRate: number;
  conversionTrend: ConversionTrend;
  topCars: { id: number; name: string; reviews: number; score: number }[];
  topCities: { city: string; count: number }[];
  topCitiesComparison?: { city: string; count: number }[];
  platformSplit: { platform: string; count: number }[];
  bookingsByOccasion: { occasion: string; count: number }[];

  // ── Booking outcomes ───────────────────────────────────────────────────
  bookingOutcomes?: { outcome: string; label: string; count: number }[];
  bookingOutcomesComparison?: { outcome: string; label: string; count: number }[];

  // ── Decline & cancellation reasons (top 10, no comparison per spec) ───
  declineReasons?: { reason_name: string; count: number }[];
  cancellationReasons?: { reason_name: string; count: number }[];
  cancellationReasonsByHost?: { reason_name: string; count: number }[];
  cancellationReasonsByRenter?: { reason_name: string; count: number }[];

  // ── Pay Now vs Pay Later ───────────────────────────────────────────────
  paymentTiming?: { timing: string; count: number }[];
  paymentTimingComparison?: { timing: string; count: number }[];
  paymentTimingTotalEligible?: number;
  paymentTimingTotalEligibleComparison?: number;

  // ── Top countries ──────────────────────────────────────────────────────
  topCountries?: { country: string; count: number }[];
  topCountriesComparison?: { country: string; count: number }[];

  // ── Avg Booking Value ──────────────────────────────────────────────────
  avgBookingValue?: number;
  avgBookingValueComparison?: number;
  avgBookingValueByMonth?: { month: string; avg: number; count: number }[];

  // ── Instant Book Rate ──────────────────────────────────────────────────
  instantBookRate?: number;
  instantBookRateComparison?: number;
  instantBookRateByMonth?: { month: string; rate: number }[];

  // ── Average Lead Time ──────────────────────────────────────────────────
  avgLeadTimeDays?: number | null;
  avgLeadTimeDaysComparison?: number | null;
  avgLeadTimeByMonth?: { month: string; avg: number }[];

  // ── Review Completion Rate ─────────────────────────────────────────────
  reviewCompletionRate?: number;
  reviewCompletionRateComparison?: number;
  reviewCompletionByMonth?: { month: string; rate: number; reviewed: number; total: number }[];

  // ── Total Bookings ─────────────────────────────────────────────────────
  totalBookings?: number;
  totalBookingsComparison?: number;
  totalBookingsByMonth?: { month: string; count: number }[];

  // ── Rate Plan Split ────────────────────────────────────────────────────
  ratePlanSplit?: { flexible: number; non_refundable: number };

  // ── Listing Supply Stats ───────────────────────────────────────────────
  listingStats?: {
    submitted: number;
    submittedComparison?: number;
    approved: number;
    approvedComparison?: number;
    deactivated: number;
    deactivatedComparison?: number;
    /** Host-initiated soft-deletes (auto_trash sweep rows excluded). */
    hostRemoved: number;
    hostRemovedComparison?: number;
    /** Listings paused by the platform after a host non-response expiry. */
    platformPaused: number;
    platformPausedComparison?: number;
    moderationTurnaroundDays: number | null;
    moderationTurnaroundDaysComparison?: number | null;
    byMonth: { month: string; submitted: number; approved: number }[];
    byMonthComparison?: { month: string; submitted: number; approved: number }[];
    snapshot: { active: number; pending: number; flagged: number; rejected: number };
  };
}


export interface PendingValuation {
  coverage?: import("../coverage-policy").CoverageState;
  id: number;
  make: string;
  model: string;
  year: number;
  insured_value: number | null;
  valuation_suggested_value: number | null;
  user_proposed_value: number | null;
  valuation_status: string;
  host_valuation_choice: "accepted_suggested" | "proposed_custom";
  estimate_source: string | null;
  estimate_confidence: string | null;
  city: string | null;
  state: string | null;
  country_code: string | null;
  /**
   * Task #1634: per-listing currency code (GBP / EUR / USD / AUD / etc.).
   * The pending-valuations review queue formats insured / proposed values
   * in this currency instead of hard-coding USD, so a UK listing's £
   * insured value isn't mis-rendered as "$".
   */
  currency_code: string | null;
  listing_status: string;
  created_at: string;
  submitted_at: string | null;
  updated_at: string;
  owner: { id: string; first_name: string; last_name: string; email: string } | null;
}


export interface AdminListParams {
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  filter?: string;
  classification?: string;
  lifecycle_state?: string;
  lifecycle_reason?: string;
}


export interface AdminListResponse<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  truncated?: boolean;
  // Task #5537 — count of listings with an OPEN held edit (owner edit to an
  // already-active listing awaiting admin review), returned on every
  // /admin/listings response so the "Pending Edits" queue tab can show a
  // badge even when another status is selected.
  held_edits_count?: number;
}


// Task #6351 — normalized image flag from the AI cover-image scan, surfaced
// by the moderation review dialog. Severity "info" entries are borderline
// observations, not violations — rendered de-emphasized.
export interface HostProfileImageFlag {
  category: string;
  severity: string;
  detail: string;
  field: string;
  excerpt: string | null;
}


export interface FlaggedHostProfileModerationLog {
  id: number;
  user_id: string;
  moderation_status: string | null;
  reasons: string[] | null;
  flagged_fields: string[] | null;
  // Task #6351 — image flags from the cover-image AI scan (normalized
  // server-side; may be absent on older/sync-gate logs).
  image_flags?: HostProfileImageFlag[] | null;
  // Task #4296 — the exact free-text the host tried to save, captured by the
  // synchronous save-time gate (keyed by field name). Present on sync_reject /
  // sync_escalation logs; null for the async runner's logs.
  proposed_content?: Record<string, unknown> | null;
  created_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  // Task #6333 — surfaced by the moderation review dialog's attempt history.
  trigger?: string | null;
  reviewer_notes?: string | null;
}


export interface FlaggedHostProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  profile_image: string | null;
  host_bio: string | null;
  car_story: string | null;
  auto_experience: string | null;
  // Task #4296 — text[] host fields added by migration 3069.
  specializations?: string[] | null;
  certifications?: string[] | null;
  cover_image: string | null;
  host_profile_moderation_status: string | null;
  updated_at: string;
  moderation_logs: FlaggedHostProfileModerationLog[];
}


export interface FlaggedHostProfilesResponse {
  profiles: FlaggedHostProfile[];
  total: number;
  page: number;
  limit: number;
  /** @deprecated Rolling-deploy alias for `total`; this feed is actionable-only. */
  actionable_total?: number;
}


// Task #6333 — per-user moderation review detail backing the
// ProfileModerationReviewDialog: live moderated-field content plus open and
// recently-resolved moderation logs.
export interface HostProfileModerationDetail {
  user: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    profile_image: string | null;
    about: string | null;
    host_bio: string | null;
    car_story: string | null;
    auto_experience: string | null;
    specializations?: string[] | null;
    certifications?: string[] | null;
    // Task #6351 — the cover image URL so the review dialog can display the
    // flagged image alongside its image flags.
    cover_image?: string | null;
    host_profile_moderation_status: string | null;
    updated_at: string;
  };
  open_logs: FlaggedHostProfileModerationLog[];
  resolved_logs: FlaggedHostProfileModerationLog[];
}


// ─── Admin host profile (Task #7827) ──────────────────────────────────────
export interface AdminHostProfile {
  user_id: number;
  about: string | null;
  host_bio: string | null;
  car_story: string | null;
  auto_experience: string | null;
  specializations: string[] | null;
  certifications: string[] | null;
  cover_image: string | null;
  profile_image: string | null;
}


// ─── Users 360° (Task #887) ────────────────────────────────────────────────
export interface User360Response {
  user: {
    publicHostPath?: string | null;
    publicHostStatus?: "direct" | "restored" | "unavailable" | "held" | "retired";
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    country_code: string | null;
    language: string | null;
    profile_image: string | null;
    created_at: string;
    last_login_at: string | null;
    /**
     * Task #5613 — reliable "last logged in": most recent recorded sign-in
     * event, falling back to the stored last_login_at. Explicit null when the
     * user has genuinely never signed in.
     */
    last_logged_in_at: string | null;
    is_active: boolean;
    is_blacklisted: boolean | null;
    deleted_at: string | null;
    deletion_pending: boolean;
    admin_role: number | null;
    admin_role_id: string | null;
    host_profile_moderation_status: string | null;
    supabase_user_id: string | null;
    merged_into_user_id: string | null;
    merged_at: string | null;
    merged_into: { id: string; email: string | null; first_name: string | null; last_name: string | null } | null;
  };
  /**
   * Mobile-app indicator: one entry per platform (ios/android) with an active
   * (unrevoked) push device — the user has the app installed and signed in
   * from it. Absent/empty ≠ definitely no app (declined push prompt).
   */
  mobile_app?: { platforms: Array<{ platform: string; last_seen_at: string | null; app_version: string | null }> };
  verification: {
    didit?: AdminDiditSummary;
    email_verified: boolean;
    phone_verified: boolean;
    identity_verified: boolean;
    renter_verified: boolean;
    id_verification_status: string | null;
    payment_method_on_file: boolean;
  };
  counts: {
    host_trips: number;
    renter_trips: number;
    host_completed: number;
    host_cancelled: number;
    renter_completed: number;
    renter_cancelled: number;
    listings: number;
    renter_experience: "first_trip" | "early_renter" | "experienced" | "power_renter";
    host_experience: "first_trip" | "early_host" | "experienced" | "power_host";
  };
  lifetime: { renter_spend: number; host_gross: number; total: number; currency: string };
  // Task #3705 — names of secondary buckets ("counts", "lifetime",
  // "manual_payouts") whose query failed to load. The figures fall back to 0,
  // so the UI must render "—" + a warning for these rather than a real 0.
  partial?: string[];
}


export interface User360RiskResponse {
  fraud_history: Array<{ created_at: string; score: number | null }>;
  payments?: {
    total_charges: number;
    failed_charges: number;
    refund_count: number;
    failure_rate: number;
    last_failure: { at: string | null; message: string | null } | null;
  };
  account?: {
    is_active: boolean;
    suspension_event_count: number;
    recent_actions: Array<{ action: string | null; at: string | null; reason: string | null }>;
  };
  devices_ip?: {
    active_devices: number;
    total_devices: number;
    distinct_ip_count: number;
    ip_match_user_count: number;
  };
  linked_accounts: Array<{
    reason: string;
    accounts: Array<{ id: string; first_name: string | null; last_name: string | null; email: string | null }>;
  }>;
  // Task #3705 — names of risk buckets ("payments", "account", "devices_ip",
  // "fraud_history", "linked_accounts") whose query failed. Failed buckets are
  // omitted/empty above so the UI must warn the admin instead of showing a
  // dangerously reassuring zero.
  partial?: string[];
}


export interface User360DevicesResponse {
  push_registration: {
    status: "no_mobile_evidence" | "permission_blocked" | "registration_missing" | "revoked" | "ownership_conflicted" | "attempted_failed" | "active" | "delivery_failing";
    evidence: string[];
    limitations: string[];
  };
  devices: Array<{
    id: number;
    expo_push_token_masked: string | null;
    platform: string | null;
    app_version: string | null;
    locale: string | null;
    device_name: string | null;
    last_seen_at: string | null;
    revoked_at: string | null;
    failure_count: number | null;
    created_at: string;
  }>;
  recent_push_sends: Array<{ id: number; status: string | null; title: string | null; created_at: string }>;
  // Web/login device history derived from user_auth_events (sign-ins etc).
  // This is the "recent login device" data; push_devices only covers mobile.
  recent_logins: Array<{
    id: string;
    event_type: string;
    ip: string | null;
    os: string;
    browser: string;
    deviceType: "mobile" | "desktop";
    created_at: string;
  }>;
}


export interface User360MessagesResponse {
  conversations: Array<{
    id: number;
    public_id: string;
    car_id: number | null;
    last_message_at: string | null;
    other_user: { id: string | number; first_name: string | null; last_name: string | null; email: string | null };
    last_message: {
      id: number;
      content: string | null;
      sender_id: string | number | null;
      is_system: boolean | null;
      system_type: string | null;
      created_at: string;
    } | null;
  }>;
}


// Task #5503 — a single message in an expanded admin conversation thread.
export interface User360ConversationMessage {
  id: number;
  content: string | null;
  sender_id: string | number | null;
  is_system: boolean | null;
  system_type: string | null;
  created_at: string;
  sender: { id: string | number; first_name: string | null; last_name: string | null; email: string | null } | null;
}

export interface User360ConversationMessagesResponse {
  messages: User360ConversationMessage[];
  // True when older messages exist beyond the capped page.
  has_more: boolean;
}


export interface User360ReviewRow {
  id: number;
  rating: number | null;
  body: string | null;
  reviewer_id: string;
  reviewee_id: string;
  trip_id: number | null;
  car_id: number | null;
  is_active: boolean;
  created_at: string;
  reviewer: { id: string | number; first_name: string | null; last_name: string | null; email: string | null } | null;
  reviewee: { id: string | number; first_name: string | null; last_name: string | null; email: string | null } | null;
}

export interface User360ReviewsResponse { authored: User360ReviewRow[]; received: User360ReviewRow[] }


export interface User360CsEvent {
  id: string;
  kind: "comm_flow" | "impersonation" | "system_message";
  title: string;
  detail: string;
  timestamp: string;
  actor?: string | null;
  metadata?: Record<string, unknown>;
}


export interface User360Payment {
  id: number;
  trip_id: number | null;
  operation: number | null;
  amount: number;
  currency: string | null;
  is_succeeded: boolean;
  error_message: string | null;
  payment_intent_id: string | null;
  charge_id: string | null;
  refund_id: string | null;
  transfer_id: string | null;
  payout_id: string | null;
  stripe_object_id: string | null;
  created_at: string;
}


export interface User360Credit {
  id: number;
  amount_cents: number;
  currency: string;
  kind: string;
  description: string | null;
  granted_by: string | null;
  trip_id: number | null;
  used_at: string | null;
  used_on_trip_id: number | null;
  expires_at: string | null;
  created_at: string;
}

export interface User360CreditsResponse {
  credits: User360Credit[];
  available: { amount_cents: number; currency: string };
}


export interface ModerationLock {
  locked_by: string;
  locked_by_name: string | null;
  locked_at: string;
  expires_at: string;
}


export interface ActiveModerationLock {
  entity_type: "listing" | "message" | "review" | string;
  entity_id: string;
  locked_by: string;
  locked_by_name: string | null;
  locked_at: string;
  expires_at: string;
}


export interface BookingVerificationRequirements {
  requires_email: boolean;
  requires_phone: boolean;
}

export type BookingVerificationMatrix = {
  driving: BookingVerificationRequirements;
  chauffeured: BookingVerificationRequirements;
  event: BookingVerificationRequirements;
};


// Task #1048 — Booking snapshot integrity audit findings.
export interface BookingSnapshotAuditMismatch {
  id: number;
  trip_id: number;
  public_id: string | null;
  mismatch_class:
    | "fee_columns_vs_suggestion"
    | "snapshot_vs_row"
    | "payment_schedule_vs_row"
    | string;
  detail: Record<string, unknown> | null;
  detected_at: string | null;
}


export interface BookingSnapshotAuditResponse {
  mismatches: BookingSnapshotAuditMismatch[];
  counts: Record<string, number>;
  total: number;
  window_days: number;
}


// Task #3351 — one-click reconcile of a snapshot-integrity mismatch.
export interface BookingSnapshotReconcileChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface BookingSnapshotReconcilePlan {
  trip_id: number;
  mismatch_class: string;
  money_affecting: boolean;
  direction: "row_from_suggestion" | "snapshot_from_row";
  changes: BookingSnapshotReconcileChange[];
  update: Record<string, unknown>;
}

export interface BookingSnapshotReconcilePreviewResponse {
  dry_run: true;
  plan: BookingSnapshotReconcilePlan;
}

export interface BookingSnapshotReconcileApplyResponse {
  success: true;
  plan: BookingSnapshotReconcilePlan;
}


// Task #2199 — Cron heartbeat health (admin Cron Health page).
export interface CronHealthEntry {
  id: string;
  name: string;
  schedule: string;
  expectedIntervalMinutes: number;
  driftThresholdSeconds: number;
  lastRunAt: string | null;
  lastDurationMs: number | null;
  lastStatus: "ok" | "failed" | null;
  lastError: string | null;
  consecutiveFailures: number;
  secondsSinceLastBeat: number | null;
  status: "healthy" | "overdue" | "failing" | "never_ran";
}


export interface CronHealthSummary {
  total: number;
  overdue: number;
  failing: number;
  neverRan: number;
  healthy: number;
}


export interface CronHealthResponse {
  entries: CronHealthEntry[];
  summary: CronHealthSummary;
  generatedAt: string;
}


// Task #4793 — System Heartbeat (silent-break detection).
export type BusinessHeartbeatStatus = "fresh" | "stale" | "overdue" | "never";

export interface BusinessHeartbeatEntry {
  key: string;
  label: string;
  source: string;
  expectedFreshnessMinutes: number;
  overdueThresholdSeconds: number;
  lastSuccessAt: string | null;
  secondsSinceLastSuccess: number | null;
  status: BusinessHeartbeatStatus;
  errored: boolean;
  /** The flow's declared UTC quiet window, if any. */
  quietHoursUtc?: { startHour: number; endHour: number };
  /**
   * True when "now" is inside the flow's quiet window — the monitor is
   * deliberately suppressing overdue pages, so the UI shows a distinct
   * "quiet hours" state instead of a raw red pill.
   */
  inQuietHours?: boolean;
}

export interface BusinessHeartbeatSummary {
  total: number;
  overdue: number;
  stale: number;
  never: number;
  fresh: number;
}

export interface BusinessHeartbeatResponse {
  entries: BusinessHeartbeatEntry[];
  summary: BusinessHeartbeatSummary;
  generatedAt: string;
}

// Task #4803 — per-flow read-only drill-down.
export interface BusinessHeartbeatDetailColumn {
  key: string;
  label: string;
}

export interface BusinessHeartbeatDetailResponse {
  key: string;
  label: string;
  source: string;
  columns: BusinessHeartbeatDetailColumn[];
  rows: Array<Record<string, unknown>>;
  errored: boolean;
  generatedAt: string;
}


// Task #2385 — GEO citation visibility tracking.
export interface GeoCitationQuery {
  id: string;
  query: string;
  category: "brand" | "category" | "model" | "city" | "use_case";
  intent: string | null;
  market: string | null;
  active: boolean;
  notes: string | null;
}


export interface GeoCitationOverview {
  windowDays: number;
  generatedAt: string;
  totals: {
    queries: number;
    activeQueries: number;
    runs: number;
    evaluableRuns: number;
    bacCited: number;
    citationRate: number;
  };
  byEngine: Record<string, { total: number; cited: number; rate: number }>;
  byCategory: Record<string, { total: number; cited: number; rate: number }>;
  topPages: Array<{ url: string; count: number }>;
  opportunities: Array<{ queryId: string; query: string; category: string; engines: string[]; competitors: string[] }>;
  trend: Array<{ week: string; total: number; cited: number; rate: number }>;
}


export interface GeoCitationResult {
  id: number;
  query_id: string;
  engine: string;
  ran_at: string;
  status: "ok" | "failed" | "manual" | "no_api";
  answer_text: string | null;
  citations: string[];
  bac_cited: boolean;
  bac_urls: string[];
  competitor_domains: string[];
  error: string | null;
}


export interface CountryEntry {
  code: string;
  carCount: number;
  enabled: boolean;
  currency: string | null;
  feePercentage: number | null;
  insuranceRequired: boolean | null;
  activeRentalTypes: string[] | null;
  paymentType: "full" | "partial";
  // Task #2595 — admin-set capture currency for partial-pay markets
  // ("USD" or "EUR"). NULL for full-pay markets (their capture currency
  // is always the local one and is not admin-configurable).
  captureCurrency: "USD" | "EUR" | null;
  config: Record<string, unknown>;
}


export interface CountryConfigUpdate {
  currency?: string;
  feePercentage?: number;
  insuranceRequired?: boolean;
  activeRentalTypes?: string[];
  paymentType?: "full" | "partial";
  // Task #2595 — admin-set capture currency for partial-pay markets.
  // Server rejects any value other than "USD" or "EUR" with 400.
  captureCurrency?: "USD" | "EUR";
}


export interface ConnectionStatus {
  dev: { ok: boolean; error?: string };
  prod: { ok: boolean; error?: string };
}


export interface SchemaDiffColumn {
  table_name: string;
  column_name: string;
  data_type: string;
  udt_name: string;
  column_default: string | null;
  is_nullable: string;
  character_maximum_length: number | null;
}


export interface SchemaDiffModifiedColumn {
  dev: SchemaDiffColumn;
  prod: SchemaDiffColumn;
  changes: string[];
}


export interface SchemaDiffIndex {
  index_name: string;
  table_name: string;
  index_definition: string;
}


export interface SchemaDiffView {
  view_name: string;
  definition: string;
}


export interface SchemaDiffFunction {
  function_name: string;
  arguments: string;
  return_type: string;
  definition: string;
}


export interface SchemaDiffConstraint {
  constraint_name: string;
  table_name: string;
  constraint_type: string;
  definition: string;
}


export interface SchemaDiffWarning {
  type: string;
  name: string;
  detail: string;
}


export interface SchemaDiffResult {
  newTables: { table_name: string }[];
  newColumns: SchemaDiffColumn[];
  modifiedColumns: SchemaDiffModifiedColumn[];
  newIndexes: SchemaDiffIndex[];
  newViews: SchemaDiffView[];
  modifiedViews: { dev: SchemaDiffView; prod: SchemaDiffView }[];
  newFunctions: SchemaDiffFunction[];
  modifiedFunctions: { dev: SchemaDiffFunction; prod: SchemaDiffFunction }[];
  newConstraints: SchemaDiffConstraint[];
  warnings: SchemaDiffWarning[];
  sql: string[];
  reverseSql: string[];
}


export interface MigrationRecord {
  number: number;
  filename: string;
  description: string;
  sql: string;
  reverseSql?: string;
  createdAt: string;
  executedAt?: string;
  status: string;
  summary?: string;
  error?: string;
}


export interface ExecutionResult {
  success: boolean;
  statementsTotal: number;
  statementsSucceeded: number;
  statementsFailed: number;
  errors: { statement: string; error: string }[];
  statementResults: { index: number; statement: string; success: boolean; error?: string; critical: boolean }[];
  summary: string;
}


export interface VerificationReport {
  dev: { tables: number; totalColumns: number };
  prod: { tables: number; totalColumns: number };
  matches: boolean;
}


export interface CodeDriftEntry {
  table: string;
  column: string;
  files: string[];
  reason: "table_missing" | "column_missing";
}


export interface CodeDriftReport {
  scannedAt: string;
  filesScanned: number;
  uniqueReferences: number;
  missing: CodeDriftEntry[];
  ignoredReferences: { table: string; column: string; reason: string }[];
  prodConnected: boolean;
  prodError?: string;
}


export interface SnapshotRecord {
  filename: string;
  createdAt: string;
  tableCount: number;
  columnCount: number;
}


export interface DangerFlag {
  severity: "warning" | "danger";
  message: string;
}


export interface OneClickResult {
  inSync: boolean;
  diff: SchemaDiffResult | null;
  dangerFlags: DangerFlag[];
  snapshot: SnapshotRecord | null;
  migration: MigrationRecord | null;
  execution: ExecutionResult | null;
  verification: VerificationReport | null;
  changelogEntry: string | null;
}


export interface SnapshotContent {
  json?: unknown;
  sql?: string;
}


// Task #6136 — last-48h booking-conversion widget (replaces the activity feed
// on the admin dashboard). Mirrors /admin/dashboard/conversion-48h.
export type ConversionLostReason = "declined" | "expired" | "cancelled" | "payment_failed" | "other";

// Task #6318 — sentinel key the API uses for lost trips with no resolvable
// cancellation reason; the UI maps it to a translated "No reason given" label.
export const CONVERSION_NO_REASON_KEY = "__no_reason__";

export interface Conversion48hSnapshot {
  since: string;
  total: number;
  converted: { count: number; pct: number };
  lost: {
    count: number;
    pct: number;
    reasons: Record<ConversionLostReason, number>;
    // Task #6318 — per-canonical-reason counts under the declined and
    // cancelled buckets (reason name as stored, or the no-reason sentinel).
    // Optional so a stale server response degrades to the flat list.
    reasonDetails?: { declined: Record<string, number>; cancelled: Record<string, number> };
  };
  pending: { count: number; pct: number };
}


export interface SystemHealthServiceStatus {
  ok: boolean;
  degraded?: boolean;
  message?: string;
}


export interface SystemHealthStatus {
  stripe: SystemHealthServiceStatus;
  database: SystemHealthServiceStatus;
  backgroundJobs: SystemHealthServiceStatus;
  stripeRegions?: Record<string, SystemHealthServiceStatus>;
  lastChecked: string;
}


export interface AlgoliaIndexHealth {
  configured: boolean;
  indexName: string;
  dbActiveListings: number;
  algoliaRecords: number | null;
  discrepancy: number | null;
  discrepancyThreshold: number;
  lastSyncAt: string | null;
  lastSnapshot: { dbActiveListings?: number; algoliaRecords?: number; discrepancy?: number; synced?: number } | null;
  warnings: string[];
  healthy: boolean;
  checkedAt: string;
}


// Task #2113 — Health snapshot for the abandoned-checkout recovery cron,
// rendered as a small tile next to Platform health and Search index in
// the admin dashboard footer.
export interface AbandonedCheckoutRecoveryHealth {
  cronId: string;
  schedule: string;
  lastTickAt: string | null;
  lastSuccessfulTickAt: string | null;
  lastSuccessfulTickAgeMinutes: number | null;
  /** Error on the latest completed tick only. Null means the current tick is healthy. */
  latestTickError: string | null;
  /** Most recent error retained for diagnosis, even after a later healthy tick. */
  mostRecentError: { message: string; tickedAt: string } | null;
  perFlow24h: Record<string, { attempted: number; succeeded: number; failed: number }>;
  last1h: { attempted: number; succeeded: number; failed: number; errorRate: number };
  last24h: { attempted: number; succeeded: number; failed: number };
  openIntents: number;
  eligibleBacklog: { "1h": number; "24h": number; "72h": number };
  backlogTrend: {
    windowHours: number;
    direction: "growing" | "shrinking" | "flat" | "unknown";
    current: { "1h": number; "24h": number; "72h": number };
    previous: { "1h": number; "24h": number; "72h": number } | null;
    currentTotal: number;
    previousTotal: number | null;
    delta: number | null;
    growthRate: number | null;
    spanHours: number | null;
    sampleCount: number;
    sustainedGrowth: boolean;
  };
  thresholds: {
    staleTickMinutes: number;
    errorRate: number;
    errorRateMinAttempts: number;
    zeroSendWindowHours: number;
    backlogTrendWindowHours: number;
    backlogGrowthMinSpanHours: number;
    backlogGrowthMinCurrent: number;
    backlogGrowthMinAbsolute: number;
    backlogGrowthMinRate: number;
  };
  warnings: string[];
  healthy: boolean;
  checkedAt: string;
}


/**
 * Read-only win-back funnel snapshot for the dashboard footer widget. Counts
 * are pulled straight from `win_back_offers` (declined-booking offers) and
 * `winback_availability_probes` (host availability probes), for both all-time
 * and the trailing 30-day window. Percentages/rates are derived on the client
 * with divide-by-zero guards.
 */
export interface WinBackFunnelStats {
  offers: {
    allTime: { sent: number; viewed: number; converted: number; expired: number };
    last30d: { sent: number; viewed: number; converted: number; expired: number };
  };
  probes: {
    allTime: { sent: number; yes: number; yesInstant: number; no: number };
    last30d: { sent: number; yes: number; yesInstant: number; no: number };
  };
  /**
   * Provider acceptance and delivery lifecycle, plus the first-party
   * click-attribution redirect used only by renter win-back emails.
   */
  email: {
    allTime: { sent: number; delivered: number; opened: number; clicked: number; bounced: number; complained: number };
    last30d: { sent: number; delivered: number; opened: number; clicked: number; bounced: number; complained: number };
  };
  checkedAt: string;
}


export interface BadgeCounts {
  pendingListings: number;
  /** Matches the default unresolved Driver Verification queue. */
  pendingDriverReviews?: number;
  /** Brand requests awaiting review and blocking linked listings. */
  pendingBrandRequests: number;
  flaggedMessages: number;
  unmoderatedReviews: number;
  activeDisputes: number;
  /** Task #606: host profiles awaiting admin moderation. */
  pendingProfileModeration: number;
  /** Task #884: triage queue size for the war-room. */
  bookingsAtRisk: number;
  /** Task #6254: pending host-cancellation requests awaiting admin review. */
  pendingHostCancellations: number;
  /** Task #7540: open end-user reports (in-app report + block) awaiting T&S triage. */
  openUserReports: number;
  /** Task #20029: DSv2 damage claims in the active pipeline (== default /admin/claims queue). */
  openClaims: number;
}


export interface FinancialReport {
  grossRevenue: number;
  platformFees: number;
  /** True platform take: service fee + host commission (payment_due − owner_earn). */
  platformRevenue: number;
  ownerPayouts: number;
  refunds: number;
  climateContributions: number;
  climateRefunds: number;
  revenueTimeline: { date: string; gross: number; fees: number; platformRevenue: number }[];
  payoutTimeline: { date: string; payouts: number; refunds: number; climateRefunds: number }[];
  topEarners: { rank: number; carName: string; ownerName: string; trips: number; revenue: number }[];
  /**
   * Task #949: point-in-time snapshot of the cash we currently owe hosts.
   * Same shape as `/admin/payouts/summary.pendingLiability` so the two
   * pages cannot drift. `null` if the underlying query failed — see
   * `partialFailures`.
   */
  pendingLiability: PendingLiability | null;
  /**
   * Task #949: forecast cash from pay-later renter charges still due.
   * Excludes anything already in our Stripe balance.
   */
  uncapturedCharges: UncapturedCharges | null;
  /**
   * Task #949: per-sub-query failures so the dashboard can degrade
   * gracefully (show what worked, surface what didn't) instead of
   * blanking the whole page on a single broken aggregate.
   */
  partialFailures: Array<{ source: string; error: string }>;
}

/**
 * Task #5598: declined-booking win-back KPIs. Offers created in the selected
 * window broken down by lifecycle status, plus engagement (page views) and
 * conversions (offers that led to a new booking). "Shelby AI" is the
 * deterministic ranker — nothing here is LLM-derived.
 */
export interface WinBackReport {
  period: { from: string; to: string };
  total: number;
  active: number;
  converted: number;
  expired: number;
  viewed: number;
  totalPageViews: number;
  conversionRatePct: number;
  viewRatePct: number;
  convertedTripIds: number[];
}


export interface UncapturedCharges {
  totalUSD: number;
  byCurrency: Record<string, number>;
  count: number;
  nextChargeDate: string | null;
  fx: {
    source: string;
    fetchedAt: string;
    stale: boolean;
    missingCurrencies: string[];
  };
}


export interface GiftCard {
  id: number;
  code: string;
  initial_value: number;
  remaining_balance: number;
  currency: string;
  status: "active" | "redeemed" | "expired" | "disabled";
  recipient_email: string | null;
  assigned_user_id: number | null;
  expires_at: string | null;
  batch_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}


export interface GiftCardTransaction {
  id: number;
  gift_card_id: number;
  amount: number;
  balance_after: number;
  trip_id: number | null;
  /** Trip UUID joined server-side so the gift-card detail can render the
   *  customer-facing short ref instead of the internal numeric trip id
   *  (task #2388). */
  trip_public_id?: string | null;
  description: string | null;
  created_by: string | null;
  created_at: string;
}


export interface GiftCardDetail extends GiftCard {
  transactions: GiftCardTransaction[];
}


export interface GiftBalanceReport {
  totalIssued: number;
  totalRemaining: number;
  totalRedeemed: number;
  activeCount: number;
  fullyUsedCount: number;
  expiredCount: number;
  totalCards: number;
  redemptionsByDay: { date: string; amount: number }[];
}


// Task #1839: renamed `PromoCode` → `Coupon` to match the underlying
// `coupons` table (the `promo_codes` table never existed in DS-dev).
// The public API URL `/admin/promo-codes` and the "Promo codes" UI label
// stay unchanged — only the TypeScript shape moves to the real columns.
export interface Coupon {
  id: number;
  code: string;
  discount_type: "percentage" | "fixed";
  discount_value: number;
  usage_limit: number | null;
  used_count: number;
  min_order_amount: number | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  /** Task #6863: ISO-4217 currency code. Non-null only for fixed-type coupons
   *  that are pinned to a specific market. NULL = currency-agnostic. */
  currency: string | null;
}


export type ContentArea = "web" | "homepage" | "mobile" | "sidebar";


export interface SiteContent {
  id: number;
  slug: string;
  title: string;
  body: string;
  content_type: string;
  content_area: ContentArea;
  locale: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}


export interface ConfigSetting {
  id: number;
  key: string;
  value: string | null;
  description: string | null;
}


export interface NotificationAuditEntry {
  id: number;
  user_id: string;
  type: string;
  title: string;
  message: string;
  channel: string;
  read: boolean;
  created_at: string;
  /** Deep-link the in-app notification opens to (when set). */
  action_url?: string | null;
  /**
   * Trip reference (from the notification's `data` payload) used to
   * re-render the actual email/SMS/push the customer received via
   * GET /admin/trips/:id/notifications. Null for non-trip notifications.
   */
  trip_id?: number | string | null;
  trip_public_id?: string | null;
}


export interface FunnelAnalytics {
  funnel: { stage: string; count: number }[];
  cohorts: { week: string; signups: number; firstBooking: number }[];
  dropoffs: { stage: string; dropoffRate: number }[];
  timeToFirstBooking: { bucket: string; count: number }[];
  retention: { month: string; rate: number }[];
}

export interface OtpFunnelAnalytics {
  available: boolean;
  period_days: number;
  minimum_volume: number;
  volume_sufficient: boolean;
  accepted_or_reused: number;
  verified: number;
  completion_rate: number | null;
  checkout: { accepted_or_reused: number; verified: number; completion_rate: number | null };
  outcomes: {
    accepted_send: number;
    valid_code_reused: number;
    fallback_link_recovery: number;
    client_cooldown_warning: number;
  };
  enforced_rate_limits: Record<"sliding_window_email" | "sliding_window_ip" | "supabase" | "sliding_window_unavailable" | "unknown", number>;
  failures: Record<"provider_error" | "expired_or_superseded" | "mismatch", number>;
  data_quality: { definition: string; exclusions: string[] };
  alert: { should_alert: boolean; reason: string | null };
}


/**
 * A Stripe object referenced by a payment/billing timeline row, carried so the
 * admin UI can deep-link the id into the correct Stripe dashboard object
 * (Task #4748). `region` selects the US vs international account; `livemode`
 * selects the live vs test dashboard. Either may be null when unresolved.
 */
export interface StripeObjectRef {
  id: string;
  region: string | null;
  livemode: boolean | null;
}

export interface TripEvent {
  id: string;
  event_type: string;
  event: string;
  datetime: string;
  details: string;
  additional_info: string;
  sending_status?: string;
  delivery_status?: string;
  /** Structured Stripe refs for deep-linking; mirrors the ids in `additional_info`. */
  stripe_refs?: StripeObjectRef[];
  author?: {
    id: number;
    first_name: string | null;
    last_name: string | null;
    profile_image: string | null;
  };
}


export type TripEventType = "Lifecycle" | "Fraud Review" | "Billing" | "Suggestion" | "Status Change" | "Notification" | "Driver Verification" | "History Log";


export interface TripMessage {
  id: number;
  conversation_id: number;
  sender_id: number | null;
  // Task #5939 — flagged bodies are delivered to admins directly (no reveal
  // step); `content` carries the real body. User-facing `is_hidden` suppression
  // for the other participant is unchanged. (`content` may still be null for
  // genuinely empty/system rows.)
  content: string | null;
  is_system: boolean;
  is_read: boolean;
  is_filtered: boolean;
  is_hidden?: boolean;
  flag_categories?: string[] | null;
  flag_reason?: string | null;
  redacted?: boolean;
  system_type: string | null;
  system_payload: Record<string, unknown> | null;
  created_at: string;
}


export interface TripConversation {
  id: number;
  public_id: string;
  user_1_id: number;
  user_2_id: number;
  car_id: number | null;
  created_at: string;
  last_message_at: string | null;
}


export interface TripMessagesResponse {
  conversations: TripConversation[];
  messages: TripMessage[];
  users: Record<number, { id: number; first_name: string; last_name: string }>;
  // Task #3694 — pagination cursor. `hasMore` ⇒ older messages exist beyond
  // this page; pass `oldestCursor` back as `before` to load the next page.
  hasMore?: boolean;
  oldestCursor?: string | null;
}


// Task #2854 — per-trip notifications panel data.
export type TripNotifChannel = "email" | "sms" | "push";

export type TripNotifRunStatus = "sent" | "skipped" | "suppressed" | "failed" | "pending" | string;

export type TripNotifChannelSummaryStatus = "sent" | "skipped" | "pending" | "failed" | "not_configured";


// Delivery / read status reported by provider callbacks (Twilio for SMS,
// Resend for email), joined to a run/ledger row by its provider message
// id. SMS tops out at "delivered" (carriers don't expose reads); email
// adds "opened"/"clicked".
export interface TripNotifDeliveryStatus {
  status: string;
  provider: string | null;
  at: string | null;
}

export interface TripNotificationRun {
  id: number;
  flowId: string;
  flowLabel: string;
  stepId: string;
  channel: TripNotifChannel;
  status: TripNotifRunStatus;
  reason: string | null;
  templateId: string | null;
  richTemplateId: string | null;
  providerMessageId: string | null;
  userId: string | null;
  audience: "renter" | "owner" | "other" | "unknown";
  contextKey: string | null;
  createdAt: string;
  /** Delivery/read status from provider callbacks. */
  deliveryStatus: TripNotifDeliveryStatus | null;
}


export interface TripNotificationChannelSummary {
  status: TripNotifChannelSummaryStatus;
  runId: number | null;
  templateId: string | null;
  reason: string | null;
  configured: boolean;
}


export interface TripNotificationFlow {
  flowId: string;
  flowLabel: string;
  trigger: string;
  description: string;
  channels: Record<TripNotifChannel, TripNotificationChannelSummary>;
}


export interface TripNotificationContent {
  channel: TripNotifChannel | "in_app";
  // Task #5386 — "snapshot" = real content persisted at send time by a
  // legacy code-defined sender.
  source: "rich_template" | "push_sends" | "template_id" | "snapshot" | "none";
  email?: { subject: string; html: string };
  sms?: { body: string };
  push?: { title: string; body: string; deepLink?: string | null };
  // Task #5462 — in-app bell reminder preview (title + body).
  in_app?: { title: string; body: string };
  note?: string;
}


// Task #3166 — payout/system notifications recorded in notifications_ledger
// (channel="in_app") and scoped to this trip. Surfaced separately from the
// flow-based runs because they carry no email/SMS/push body.
export interface TripLedgerEntry {
  id: number;
  channel: string;
  event: string | null;
  status: string;
  reason: string | null;
  userId: number | null;
  /** Original recipient address (the ledger row's `recipient` column). */
  recipient: string | null;
  metadata: Record<string, unknown> | null;
  /** Task #3182 — provider message id (Resend/Twilio), when the send produced one. */
  providerMessageId: string | null;
  createdAt: string;
  /** Delivery/read status from provider callbacks. */
  deliveryStatus: TripNotifDeliveryStatus | null;
  /**
   * Task #5386 — rendered preview content built from the row's persisted
   * content snapshot (legacy code-defined sends). Null for rows that carry
   * no body (payout/system rows) or that predate snapshotting.
   */
  content: TripNotificationContent | null;
  /**
   * True when this email was actually dispatched and carries a replayable
   * content snapshot, so the panel can offer a "Resend" action that re-sends
   * the exact same content to the original recipient.
   */
  resendable: boolean;
}


export interface TripNotificationsResponse {
  tripId: number;
  publicId: string | null;
  shortRef: string | null;
  runs: TripNotificationRun[];
  flows: TripNotificationFlow[];
  content: Record<number, TripNotificationContent>;
  /** Task #3166 — payout/system ledger entries scoped to this trip. */
  ledgerEntries: TripLedgerEntry[];
}

export interface Airport {
  [key: string]: unknown;
  id: number;
  name: string;
  iata_code: string;
  city: string | null;
  state: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
  is_active: boolean;
}


export interface AirportsSyncResult {
  added: number;
  updated: number;
  deactivated: number;
  reactivated: number;
  unchanged: number;
  totalFromSource: number;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  source: string;
}


export interface VehicleMake {
  id: number;
  name: string;
  is_premier: boolean;
  is_exotic: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}


export interface SearchScoringWeight {
  key: string;
  label: string;
  description?: string;
  value: number;
  default: number;
  min: number;
  max: number;
  step: number;
}


export interface SeoPageMetadata {
  id: number;
  page_path: string;
  meta_title: string | null;
  meta_description: string | null;
  canonical_url: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  created_at: string;
  updated_at: string;
}


export interface VehicleModel {
  id: number;
  make_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}


export interface VehicleSubmodel {
  id: number;
  model_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}


export interface VehicleMakesPage {
  makes: VehicleMake[];
  total: number;
  page: number;
  limit: number;
}


export type DriverViewSlug = "all" | "awaiting_review" | "ai_flagged" | "mvr_fail" | "cross_match" | "resubmissions" | "host_invited";


// Task #8211 — scoped booking driver context returned by GET /admin/trips/:id.
// Non-US / out-of-scope bookings only ever carry `{ scope: "excluded" }`.
export interface AdminDriverAssignmentView {
  id: number;
  status: string;
  effective_state: "invited" | "accepted" | "verification_required" | "confirmed" | "verification_stale" | "expired";
  source: string | null;
  driver_role: string | null;
  invited_email: string | null;
  invited_at: string;
  expires_at: string | null;
  accepted_at: string | null;
  confirmed_at: string | null;
  driver_id: number | null;
  driver_user_id: number | null;
  owner_driver_id: number | null;
  verification: {
    current: boolean;
    verified_at: string | null;
    licence_status: number | null;
    mvr_status: string | null;
    evidence: {
      has_front: boolean;
      has_back: boolean;
      purge_after: string | null;
      retention_hold: { reason: string; set_at: string | null } | null;
    };
  } | null;
}

export interface AdminDiditSummary {
  state: "not_started" | "in_progress" | "awaiting_review" | "approved" | "rejected" | "expired" | "unavailable";
  completed: boolean | null;
  completed_at: string | null;
  recorded_outcome: string | null;
  driver_id: number | null;
}

export interface AdminDriverRosterRow {
  key: string;
  role: string;
  active: boolean;
  identity: { user_id: number; name: string; email: string | null } | null;
  display_name?: string;
  invited_email: string | null;
  invitation: string;
  accepted_at: string | null;
  driver_id: number | null;
  didit: AdminDiditSummary;
  samba: { state: string; applicable: boolean | null };
  alternative_check: { label: string; state: string } | null;
  clearance: string;
  evidence: { has_front: boolean; has_back: boolean };
}

export type AdminBookingDriverContext =
  | { scope: "excluded" }
  | {
      scope: "us_self_drive" | "unresolved_legacy_self_drive";
      roster?: AdminDriverRosterRow[];
      market: "US";
      rental_type: string;
      driver_source: "renter_primary" | "renter_nominated" | "unresolved_legacy";
      assignment: AdminDriverAssignmentView | null;
    }
  | {
      scope: "us_host_driven";
      roster?: AdminDriverRosterRow[];
      market: "US";
      rental_type: string;
      driver_source: "host_default" | "host_invited" | "legacy_assigned";
      host_presumed_driver: boolean;
      actionable: boolean;
      assignment: AdminDriverAssignmentView | null;
      legacy_assigned_driver_id: number | null;
      host_identity: {
        id: number;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
      } | null;
      resolved_driver_identity: {
        id: number;
        user_id: number;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
      } | null;
    };


export interface AdminDriver {
  id: number;
  user_id: string;
  license_verification_status: number | null;
  license_status_label: string;
  license_image_url: string | null;
  is_active: boolean;
  mvr_status: string | null;
  mvr_completed_at: string | null;
  mvr_order_id: string | null;
  created_at: string;
  updated_at: string | null;
  trips_count: number;
  cross_match_count: number;
  fraud_score: number | null;
  /** Task #8211 — latest host-invited alternate booking assignment (host_invited view). */
  host_invited_assignment?: { assignment_id: number; trip_id: number; rental_type: string; status: string; created_at: string } | null;
  kyc_count: number;
  latest_kyc: { status: string | null; document_type: string | null; created_at: string | null } | null;
  last_moderation: { action: string; created_at: string } | null;
  user: { id: string; first_name: string | null; last_name: string | null; email: string | null; profile_image: string | null } | null;
}


export interface DriverTimelineEvent {
  kind: "kyc" | "mvr" | "moderation";
  at: string;
  title: string;
  description: string | null;
  status: string | null;
  actor: string | null;
  impersonated: boolean;
  payload?: Record<string, unknown> | null;
}


export interface DriverCrossMatch {
  driver_id: number | null;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  created_at: string;
  signal: string;
}


export interface DriverKycRow {
  id: number;
  type: string | null;
  document_type: string | null;
  status: string | null;
  reason: string | null;
  provider: string | null;
  provider_ref: string | null;
  front_document_url: string | null;
  back_document_url: string | null;
  verified_at: string | null;
  created_at: string;
}


export interface DriverDetailData {
  driver: Record<string, unknown> & {
    id: number;
    user_id: string;
    license_verification_status: number | null;
    license_status_label: string;
    license_image_url: string | null;
    is_active: boolean;
    mvr_status: string | null;
    created_at: string;
    user: { id: string; first_name: string | null; last_name: string | null; email: string | null; phone: string | null; profile_image: string | null; created_at: string; renter_verified: boolean } | null;
  };
  images: {
    front_archived?: boolean;
    back_archived?: boolean;
    note?: string | null;
  };
  trips: { id: number; public_id: string; status: string; started_at: string; drop_off: string; created_at: string; car: { make: string; model: string; year: number } | null }[];
  kyc: DriverKycRow[];
  mvr: { id: number; order_id: string; status: string | null; result: Record<string, unknown> | null; created_at: string }[];
  audit: { id: number; action: string; changed_by: string | null; old_value: Record<string, unknown> | null; new_value: Record<string, unknown> | null; impersonation_session_id: string | null; created_at: string }[];
  timeline: DriverTimelineEvent[];
  cross_matches: DriverCrossMatch[];
  fraud: { score: number | null; reasons: string[]; computed_at: string | null } | null;
}


export interface BulkApproveResult {
  success: boolean;
  approved: number[];
  skipped: { driver_id: number; reason: string; score?: number }[];
  max_score: number;
}


export interface BlacklistEntry {
  id: number;
  entry_type: "email" | "ip" | "phone";
  value: string;
  reason: string | null;
  admin_user_id: string | null;
  created_at: string;
  // Task #3433 — distinguish operator permanent bans from self-expiring
  // adaptive auto-bans. `source` is null/"manual" for operator bans and
  // "auto"/"auto:N" for adaptive auto-bans; `expires_at` is null for permanent
  // bans and an ISO timestamp for temporary ones.
  expires_at: string | null;
  source: string | null;
  admin: { id: string; first_name: string | null; last_name: string | null; email: string | null } | null;
}


export interface RatingRecalcResult {
  success: boolean;
  totalCars: number;
  updated: number;
  failed: number;
}


export interface VinInfo {
  vin: string;
  valid: boolean;
  wmi: string | null;
  vds: string | null;
  vis: string | null;
  modelYear: string | null;
  plantCode: string | null;
  checkDigit: string | null;
}


export interface VinMatch {
  id: number;
  make: string;
  model: string;
  year: number;
  vin: string;
  listing_status: string;
  owner: { id: string; first_name: string; last_name: string } | null;
}


export interface InngestFunction {
  id: string;
  name: string;
  trigger: string;
  schedule: string;
  description: string;
}


export interface PushSendResult {
  success: boolean;
  sent: number;
  total: number;
}


// Programmatic GEO admin API helpers.
export type ProgrammaticGeoClusterId =
  | "model_city" | "brand_city" | "usecase_city" | "decade_city";


export interface ProgrammaticGeoCluster {
  id: ProgrammaticGeoClusterId;
  label: string;
  pageType: string;
  category: string;
  qualityGate: { minInventory: number; minListingsWithImage: number; minReviews: number };
  // Kill-switch + gate override surfaced from
  // programmatic_geo_cluster_settings (null when no row exists yet).
  enabled?: boolean;
  gateOverride?: Record<string, number> | null;
  settingsUpdatedAt?: string | null;
  settingsUpdatedBy?: string | null;
}


export interface ProgrammaticGeoPage {
  id: number;
  full_path: string;
  language: string;
  locale: string | null;
  cluster: ProgrammaticGeoClusterId;
  cluster_inputs: Record<string, unknown> | null;
  name: string;
  page_title: string | null;
  is_indexable: boolean;
  active: boolean;
  quality_snapshot: {
    inventory: number;
    listingsWithImage: number;
    minPrice: number | null;
    maxPrice: number | null;
    currency: string | null;
    topMakes: string[];
    takenAt: string;
  } | null;
  quality_passed: boolean | null;
  redirect_to_path: string | null;
  last_snapshot_at: string | null;
  updated_at: string;
  // Per-row override knobs surfaced to the admin tab.
  editorial_override?: boolean | null;
  demand_signal?: number | null;
}


export interface ProgrammaticGeoComboResult {
  fullPath: string;
  status: "created" | "updated" | "redirected" | "unchanged" | "error";
  inventory: number;
  qualityPassed: boolean;
  redirectTo?: string;
  message?: string;
}


export interface ProgrammaticGeoGenerateResponse {
  ok: true;
  dryRun: boolean;
  cluster: ProgrammaticGeoClusterId;
  combos: number;
  created: number;
  updated: number;
  retired: number;
  errors: number;
  results: ProgrammaticGeoComboResult[];
}


export interface SitemapPreview {
  baseUrl: string;
  staticPages: number;
  carPages: number;
  landingPages: number;
  totalUrls: number;
  sitemapUrl: string;
}


export interface SearchAnalyticsTotals {
  clicks: number;
  impressions: number;
  /** Click-through rate as a 0–1 fraction. */
  ctr: number;
  /** Average position (1 = top of results). */
  position: number;
}

export interface SearchAnalyticsRowSummary extends SearchAnalyticsTotals {
  /** The query text or page URL for the row. */
  key: string;
}

export interface SearchAnalyticsResponse {
  configured: boolean;
  property: string;
  dateRange?: { startDate: string; endDate: string; days: number };
  totals?: SearchAnalyticsTotals;
  topQueries?: SearchAnalyticsRowSummary[];
  topPages?: SearchAnalyticsRowSummary[];
  error?: string;
}


// Task #4517 — Google Indexing submission log. One row per Indexing API call
// recorded by lib/google-indexing.ts.
export interface IndexingLogRow {
  id: number;
  car_id: number | null;
  event_name: string | null;
  url: string | null;
  notification_type: string | null;
  ok: boolean;
  skipped: boolean;
  reason: string | null;
  http_status: number | null;
  error: string | null;
  created_at: string;
}


export interface IndexingLogTotals {
  total: number;
  succeeded: number;
  skipped: number;
  failed: number;
  quotaRejected: number;
}


export interface IndexingLogResponse {
  configured: boolean;
  recent: IndexingLogRow[];
  totals: IndexingLogTotals | null;
  error?: string;
}


export interface InspectionPhoto {
  id: number;
  url: string;
  caption: string | null;
}


export interface Inspection {
  id: number;
  car_id: number;
  trip_id: number | null;
  inspection_type: "pickup" | "dropoff" | "periodic";
  status: "passed" | "issues_found" | "failed";
  inspection_date: string;
  inspector_name: string;
  mileage_reading: number | null;
  fuel_level: string | null;
  condition_notes: string | null;
  damage_observations: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  photos: InspectionPhoto[];
}


export interface AdminSuggestion {
  id: number;
  trip_id: number;
  suggested_by: string;
  start_date: string;
  end_date: string;
  rental_fee: number;
  service_fee: number;
  insurance_fee: number;
  delivery_fee: number;
  deposit: number;
  payment_due: number;
  owner_earn: number;
  status: string;
  created_at: string;
  trip: {
    id: number;
    public_id: string;
    status: string;
    // Task #4661 — primary_image embed so the suggestions list/detail can
    // render the shared car thumbnail via `carPhotoUrl`.
    car: { id: number; make: string; model: string; year: number; primary_image?: AdminCarImage[] | null } | null;
    renter: { id: string; first_name: string; last_name: string; email: string } | null;
    owner: { id: string; first_name: string; last_name: string; email: string } | null;
  } | null;
}


export interface SuggestionDetail extends AdminSuggestion {
  insurance_package_name: string | null;
  cancellation_policy: string | null;
  rental_type: number | null;
}


export interface SuggestionHistoryEntry {
  id: number;
  status: string;
  rental_fee: number;
  payment_due: number;
  created_at: string;
}


export interface CancelDeclineReason {
  id: number;
  reason: string;
  details_required: boolean;
  applies_to: "cancel" | "decline" | "withdrawal";
  is_active: boolean;
  sort_order: number;
  reason_flow?: "cancellation" | "decline" | "withdrawal" | "legacy_withdrawal";
}


export interface NewsletterSubscriber {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  status: string;
  source: string;
  subscribed_at: string;
  unsubscribed_at: string | null;
}


export interface NewsletterSubscribersResponse {
  subscribers: NewsletterSubscriber[];
  total: number;
  totalSubscribers: number;
  activeSubscribers: number;
  page: number;
  limit: number;
}


export interface NewsletterStats {
  growth: { date: string; subscribed: number; unsubscribed: number }[];
  sources: { source: string; count: number }[];
}


// ─── Concierge leads (Task #1036) ──────────────────────────────────────
// Sales-team triage view backed by the `concierge_leads` table that the
// search-page banner writes to. See routes/admin.ts.

export type ConciergeLeadStatus = "new" | "contacted" | "closed";

export type ConciergeLeadSource = "hero" | "inline" | string;


export interface ConciergeLead {
  id: number;
  full_name: string;
  work_email: string;
  phone: string | null;
  looking_for: string;
  desired_date: string | null;
  location: string | null;
  notes: string | null;
  source: ConciergeLeadSource;
  result_count: number | null;
  locale: string | null;
  intercom_delivered: boolean;
  intercom_contact_id: string | null;
  intercom_conversation_id: string | null;
  intercom_ticket_id: string | null;
  intercom_ticket_status: string;
  intercom_ticket_created_at: string | null;
  intercom_ticket_error: string | null;
  source_path: string | null;
  user_agent: string | null;
  ip: string | null;
  user_id: string | null;
  status: ConciergeLeadStatus;
  admin_notes: string | null;
  created_at: string;
}


export interface ConciergeLeadsResponse {
  leads: ConciergeLead[];
  total: number;
  page: number;
  limit: number;
  summary: {
    total: number;
    newCount: number;
    intercomDelivered: number;
  };
}


export interface ConciergeLeadsQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: ConciergeLeadStatus | "";
  source?: "hero" | "inline" | "";
  intercom_delivered?: "yes" | "no" | "";
  date_from?: string;
  date_to?: string;
  sort?: string;
}


export interface HistoryLog {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  changed_by: string | null;
  changes: Record<string, unknown> | null;
  created_at: string;
  ip_address: string | null;
}


export interface ImageUploadTestResult {
  step: string;
  status: string;
  details: string;
  duration: number;
}


export interface InsuranceProvider {
  id: number;
  name: string;
  contact: string | null;
  regions: string | null;
}


export interface InsurancePackage {
  id: number;
  name: string;
  description: string | null;
  coverage_liability: boolean;
  coverage_physical: boolean;
  coverage_roadside: boolean;
  owner_liability_coverage: string | null;
  renter_liability_coverage: string | null;
  physical_deductible: string | null;
  liability_deductible: string | null;
  full_comp: boolean | null;
  customer_support: boolean | null;
}


export interface InsurancePricing {
  id: number;
  insurance_package_id: number;
  car_value_range_id: number | null;
  rental_type: string;
  daily_rate: number;
  liability_cost: number | null;
  physical_cost: number | null;
  price: number | null;
  package_id: number | null;
  value_range_id: number | null;
}


export interface HourlyDiscount {
  id: number;
  hours: number;
  discount_percentage: number;
  insurance_package_id: number | null;
}


export interface CarValueRange {
  id: number;
  min_value: number;
  max_value: number;
  label: string | null;
}


export interface BulkImportRow {
  rowNum: number;
  tripId: string;
  rentalStartDate: string;
  make: string;
  model: string;
  year: string;
  state: string;
  insuredValue: number;
  rentalDays: number;
  rentalHours: number;
  rentalHoursWithInsurance: number;
  rentalType: string;
  rateType: string;
  insurancePackage: string;
  insurancePackageId?: number | null;
  discountInsurance: number;
  cfCost: number;
  cfPerDay?: number;
  packagePrice: number;
  packagePerDay?: number;
  finalCharge: number;
  insuranceLiabilityCost?: number;
  insurancePhysicalCost?: number;
  marginDollars: number;
  marginPercent: number;
  error: string | null;
}


export type BulkImportOutcomeStatus = "created" | "updated" | "skipped" | "errored";


export interface BulkImportOutcome {
  rowNum: number;
  tripId: string;
  status: BulkImportOutcomeStatus;
  reason: string | null;
  suggestionId: number | null;
  resolvedTripId: number | null;
}


export interface BulkImportCommitMeta {
  idempotencyKey: string;
  alreadyCommitted: boolean;
  batchId: number | null;
  committedAt: string | null;
  counts: { created: number; updated: number; skipped: number; errored: number };
  outcomes: BulkImportOutcome[];
}


export interface BulkImportSummary {
  totalBookings: number;
  totalRevenue: number;
  totalCfCost: number;
  totalMargin: number;
  totalMarginPercent: number;
  lossBookings: number;
  totalRows: number;
  errorRows: number;
}


export interface BulkImportResponse {
  results: BulkImportRow[];
  errors: { row: number; message: string }[];
  summary: BulkImportSummary;
}


export interface BulkImportCommitResponse extends BulkImportResponse {
  commit: BulkImportCommitMeta;
}


// ---------------------------------------------------------------------------
// Task #3038 — Admin CSV booking import (legacy BAC export).
// ---------------------------------------------------------------------------

export type BookingImportConfidence = "exact" | "probable" | "ambiguous" | "none";


export interface BookingImportBatch {
  id: number;
  filename: string;
  status: "staged" | "approved" | "partial" | "rejected";
  row_count: number;
  notes: string | null;
  created_at: string;
  updated_at?: string;
  uploaded_by?: number | null;
}


export interface BookingImportRow {
  id: number;
  batch_id: number;
  row_num: number;
  legacy_id: string;
  raw: Record<string, string>;
  matched_renter_id: number | null;
  matched_owner_id: number | null;
  matched_car_id: number | null;
  match_confidence: BookingImportConfidence | null;
  match_notes: string | null;
  status: "pending" | "ready" | "approved" | "rejected" | "errored";
  reject_reason: string | null;
  promoted_trip_id: number | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}


export interface BookingImportPreviewRow {
  rowNum: number;
  legacyId: string;
  customerEmail: string | null;
  customerName: string | null;
  carExternalId: string | null;
  carDisplay: string | null;
  carEmail: string | null;
  bookingStart: string | null;
  bookingEnd: string | null;
  priceBookingTotal: number | null;
  currency: string | null;
  state: "confirmed" | "draft";
  rentalType: string | null;
  eventType: string | null;
  match: {
    matchedRenterId: number | null;
    matchedOwnerId: number | null;
    matchedCarId: number | null;
    confidence: BookingImportConfidence;
    notes: string;
  } | null;
}


export interface BookingImportPreviewResponse {
  rowCount: number;
  warnings: { row: number; message: string }[];
  rows: BookingImportPreviewRow[];
}


export interface OperationalReportResult {
  items: Record<string, unknown>[];
  total: number;
  [key: string]: unknown;
}

export interface RentalSummaryFilters {
  statuses?: string[];
  countries?: string[];
  paymentTypes?: ("partial" | "full")[];
  bookingTypes?: ("instant" | "request")[];
  bookedFrom?: string;
  bookedTo?: string;
  rentalFrom?: string;
  rentalTo?: string;
  page?: number;
  pageSize?: number;
}

export interface RentalSummaryTotals {
  tripCount?: number;
  gross: number;
  discounts: number;
  insurance: number;
  delivery: number;
  fees: number;
  taxes: number;
  paid: number;
  refunds: number;
  outstanding: number;
  platform_revenue: number;
  owner_payout: number;
  owner_payout_paid: number;
  platform_insurance_cost?: number;
  stripe_cost?: number;
  net_revenue?: number | null;
}

export type RentalSummaryValueStatus = "applicable" | "not_applicable" | "unavailable";

export interface RentalSummaryItem extends Record<string, unknown> {
  id: number | string;
  booked_at?: string | null;
  rental_start?: string | null;
  rental_end?: string | null;
  rental_timezone?: string | null;
  location_timezone?: string | null;
  renter_insurance?: number | null;
  renter_insurance_status?: RentalSummaryValueStatus;
  host_insurance?: number | null;
  host_insurance_status?: RentalSummaryValueStatus;
  platform_insurance_cost?: number | null;
  platform_insurance_cost_status?: RentalSummaryValueStatus;
  insurance_al?: number | null;
  insurance_al_status?: RentalSummaryValueStatus;
  insurance_apd?: number | null;
  insurance_apd_status?: RentalSummaryValueStatus;
  platform_insurance_base?: number | null;
  platform_insurance_dil?: number | null;
  insurance_rental_days?: number | null;
  stripe_cost?: number | null;
  stripe_cost_status?: RentalSummaryValueStatus;
  platform_take?: number | null;
  platform_take_by_currency?: string;
  platform_take_status?: RentalSummaryValueStatus;
  owner_payout_currency?: string;
  expected_owner_payout?: string;
  net_revenue?: number | null;
  net_revenue_status?: RentalSummaryValueStatus;
}

export interface RentalSummaryResult extends OperationalReportResult {
  items: RentalSummaryItem[];
  page: number;
  pageSize: number;
  totalPages: number;
  totals: { tripCount: number; currencies: Record<string, RentalSummaryTotals> };
  usdTotals: {
    totals: RentalSummaryTotals;
    missingCurrencies: string[];
    incompleteInputs?: string[];
    fx: { base: string; source: string | null; fetchedAt: string | null; stale: boolean | null };
  };
  partial?: boolean;
  unavailableStages?: Array<"suggestion_enrichment" | "billing_enrichment" | "totals_rpc" | "financial_inputs">;
  incompleteInputs?: string[];
}

export interface CommsEmailDef {
  id: string;
  name: string;
  audience: string;
  category: string;
  subject: string;
  description: string;
  variables: string[];
  hasCta: boolean;
  triggerEvent: string;
}


export interface CommsSmsDef {
  id: string;
  audience: string;
  trigger: string;
  message: string;
  charCount: number;
  segments: number;
  variables: string[];
}


export interface CommsPushDef {
  id: string;
  audience: string;
  trigger: string;
  title: string;
  body: string;
  deepLink: string;
  icon: string;
  badge: boolean;
  variables: string[];
}


export type CommsTriggerKind = "route" | "webhook" | "event" | "cron" | "manual";

export interface CommsTriggerSource {
  kind: CommsTriggerKind;
  method?: string;
  path?: string;
  provider?: string;
  event?: string;
  schedule?: string;
  cronId?: string;
  manualLabel?: string;
}

export interface CommsCodeRef { file: string; line?: number }

export type CommsChannelStatus = "wired" | "orphan" | "disabled";


export interface CommsFlowDef {
  id: string;
  label: string;
  category: string;
  trigger: string;
  audience: string;
  emailId: string | null;
  smsId: string | null;
  pushId: string | null;
  triggerSource: CommsTriggerSource;
  whyItFires: string;
  codeRef?: CommsCodeRef | null;
  emailWired: boolean;
  smsWired: boolean;
  pushWired: boolean;
}


export interface CommsCronJobDef {
  id: string;
  name: string;
  schedule: string;
  cadence?: string;
  description: string;
  relatedEmails: string[];
  relatedSms: string[];
  relatedPush: string[];
  notifiesUsers: boolean;
  noNotificationsReason?: string;
  lastRunStatus: string;
  lastRunAt: string | null;
  nextRunAt: string | null;
}


export interface CommsOrphanEntry {
  flowId: string;
  flowLabel: string;
  category: string;
  audience: string;
  channel: "email" | "sms" | "push";
  templateId: string;
  reason: string;
}


export interface CommsCronMapping {
  cronId: string;
  cronName: string;
  schedule: string;
  cadence: string;
  flows: { id: string; label: string; category: string; audience: string }[];
}


export interface CommsNotifPref {
  key: string;
  channel: string;
  label: string;
  enabled: number;
  disabled: number;
  total: number;
  pctEnabled: number;
}


export interface CommsDashboard {
  totalEmails: number;
  totalSms: number;
  totalPush: number;
  totalFlows: number;
  totalCronJobs: number;
  categories: string[];
  audienceCounts: Record<string, number>;
  coverageMatrix: {
    id: string;
    label: string;
    category: string;
    audience: string;
    trigger: string;
    triggerSource: CommsTriggerSource;
    whyItFires: string;
    codeRef: CommsCodeRef | null;
    hasEmail: boolean;
    hasSms: boolean;
    hasPush: boolean;
    emailStatus: CommsChannelStatus;
    smsStatus: CommsChannelStatus;
    pushStatus: CommsChannelStatus;
  }[];
}


export interface CommsUserPrefs {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  notification_email_messages: boolean;
  notification_email_booking_updates: boolean;
  notification_email_rental_reminder: boolean;
  notification_email_rate_experience: boolean;
  notification_email_promotions: boolean;
  notification_sms_messages: boolean;
  notification_sms_booking_updates: boolean;
  notification_sms_rental_reminder: boolean;
  notification_push_booking_updates: boolean;
  notification_push_rental_reminder: boolean;
  notification_push_messages: boolean;
  notification_push_rate_experience: boolean;
  notification_push_promotions: boolean;
}


export interface BookingCalcSuggestion {
  id: number;
  trip_id: number;
  status: string;
  rental_fee: number;
  service_fee: number;
  insurance_fee: number;
  delivery_fee: number;
  deposit: number;
  owner_earn: number;
  payment_due: number;
  insurance_package: number | null;
  insurance_package_name: string | null;
  insurance_liability_cost: number | null;
  insurance_physical_cost: number | null;
  insured_value: number | null;
  pick_up: string | null;
  drop_off: string | null;
  cancellation_policy: string | null;
  notes: string | null;
  suggested_car_id: number | null;
  created_at: string;
  trip: {
    id: number;
    public_id: string;
    status: string;
    rental_type: string | null;
    car: {
      id: number;
      make: string;
      model: string;
      year: number;
      city: string;
      state: string;
      country_code: string;
      insured_value: number;
      currency_code: string;
    } | null;
  } | null;
}


export interface InsuranceConfigData {
  car_value_ranges: Array<{
    id: number;
    value_from: number;
    value_till: number;
    display_order: number;
    is_active?: boolean;
  }>;
  insurance_pricing: Array<{
    id: number;
    package_id: number;
    value_range_id: number | null;
    rental_type: number;
    price: number;
    liability_cost: number;
    physical_cost: number;
    is_active?: boolean;
  }>;
  state_multipliers: Record<string, number>;
  markup_multipliers: Record<string, number>;
  floor_amount: number;
  hourly_discounts: Array<{
    hours: number;
    discount_percentage: number;
    insurance_package_id: number | null;
  }>;
}


export interface BookingCalcConfigResponse {
  packages: { id: number; name: string; is_active: boolean }[];
  config: InsuranceConfigData;
}


export interface InsuranceCalcResult {
  liability_cost: number;
  physical_cost: number;
  cf_total_cost: number;
  base_package_price: number;
  state_multiplier: number;
  state_name: string;
  package_price_after_state: number;
  discount_pct: number;
  discount_source: string;
  final_charge: number;
  floor_triggered: boolean;
  margin_dollars: number;
  margin_pct: number;
  error?: string;
}


export interface FeatureFlag {
  // `id` is null for declared-but-unseeded flags (no DB row yet). The admin
  // UI upserts these by `flag_key` on first toggle/save.
  id: number | null;
  flag_key: string;
  display_name: string;
  description: string | null;
  enabled: boolean;
  rollout_mode: "global" | "percentage" | "targeted";
  percentage: number;
  targeted_user_ids: string[];
  targeted_email_patterns: string[];
  created_at: string | null;
  updated_at: string | null;
  // True when this flag is backed by a real `feature_flags` row; false for
  // merged-in declared keys that haven't been seeded yet.
  persisted?: boolean;
}


export interface FeatureFlagAnalytics {
  flagKey: string;
  period: string;
  totalEvaluations: number;
  uniqueUsers: number;
  enabledCount: number;
  disabledCount: number;
  enableRate: number;
  timeline: Array<{ date: string; enabled: number; disabled: number; total: number }>;
}


export interface BulkActionResult<TId = number | string> {
  succeeded: TId[];
  failed: { id: TId; error: string }[];
}


// ────────────────────────────────────────────────────────────────────────────
// Flow Builder
// ────────────────────────────────────────────────────────────────────────────

export interface FlowBuilderChannelConfig {
  enabled: boolean;
  templateId: string;
  preferenceKey?: string;
  /** Optional reference to a rich template stored in comm_rich_templates.
   *  Purely additive — runtime ignores this field and continues to use templateId. */
  richTemplateId?: string;
}

export interface FlowBuilderStep {
  id: string;
  label: string;
  delayMinutes: number;
  enabled: boolean;
  channels: { email?: FlowBuilderChannelConfig; sms?: FlowBuilderChannelConfig; push?: FlowBuilderChannelConfig };
  audienceFilters?: Array<{ field: string; op: string; value?: unknown }>;
  maxSendsPerUser?: number;
}

export interface FlowBuilderDefinition {
  id: string;
  label: string;
  category: string;
  description?: string;
  trigger: { kind: "cron" | "event" | "manual" | "route"; detail?: string };
  steps: FlowBuilderStep[];
  globalQuietHours?: { startHour: number; endHour: number };
  frequencyCapPerDay?: number;
  conversionEvent?: string;
}

export interface FlowStepPreview {
  delayMinutes: number;
  channels: { email: boolean; sms: boolean; push: boolean };
}

export interface FlowBuilderRow {
  id: string;
  label: string;
  category: string;
  status: "live" | "draft" | "paused";
  is_kill_switched: boolean;
  trigger_kind: string;
  trigger_detail: string | null;
  description: string | null;
  updated_at: string;
  live_version: number | null;
  steps_preview: FlowStepPreview[] | null;
}

export interface FlowVersionRow {
  id: number;
  version: number;
  status: "draft" | "live" | "archived";
  notes: string | null;
  created_by: string | null;
  created_at: string;
  published_at: string | null;
  scheduled_publish_at: string | null;
}

export interface FlowMissingRichTemplateRef {
  stepId: string;
  channel: "email" | "sms" | "push";
  richTemplateId: string;
}

export interface FlowBuilderTemplateMetric {
  key: string;
  kind: "rich" | "legacy";
  templateId: string;
  channel: string;
  sends: number;
  delivered: number;
  opened: number;
  clicked: number;
  converted: number;
  failed: number;
  suppressed: number;
}

export interface RichTemplateMetric {
  sends: number; delivered: number; opened: number; clicked: number; converted: number; failed: number; suppressed: number;
}


export interface FlowSimulatorPreset {
  id: string;
  label: string;
  description: string;
  triggers: string[];
  contextKey: string;
  locale?: string;
  data: Record<string, unknown>;
}


export interface FlowAbTestRow {
  id: number;
  flow_id: string;
  step_id: string;
  variant_a: Record<string, unknown>;
  variant_b: Record<string, unknown>;
  split_a: number;
  status: "running" | "promoted" | "stopped";
  promoted_variant: "a" | "b" | null;
  conversion_metric: string;
  started_at: string;
  promoted_at: string | null;
  notes: string | null;
  counts: { exposuresA: number; exposuresB: number; conversionsA: number; conversionsB: number; rateA: number; rateB: number };
}


// ─── Legal Pages (Terms / Privacy / Policies / Procedures FAQ) ──────────────
export type LegalBlock =
  | { p: string }
  | { strong: string }
  | { h3: string }
  | { ul: Array<string | { text: string; items?: unknown[] }> }
  | { ol: Array<string | { text: string; items?: unknown[] }> }
  | { note: string };

export interface LegalSection { id: string; title: string; body: LegalBlock[]; }

export interface LegalFaqItem { id: string; q: string; a: LegalBlock[]; }

export interface LegalContent {
  title: string;
  seo_description?: string;
  last_updated?: string;
  intro?: LegalBlock[];
  sections: LegalSection[];
  items?: LegalFaqItem[];
  jsonLd?: Record<string, unknown>;
}

export interface LegalPageMeta {
  page_key: string;
  locale: string;
  updated_at: string;
  updated_by_email: string | null;
  current_revision_id: number | null;
}

export interface LegalRevisionSummary {
  id: number;
  change_note: string | null;
  created_at: string;
  created_by_email: string | null;
  requires_reconsent?: boolean;
}

export interface LegalRevisionDetail extends LegalRevisionSummary {
  page_key: string;
  locale: string;
  content: LegalContent;
}


// ─── Flow Studio (Mailchimp-style messaging studio) ─────────────────────────
export interface FlowStudioBrandKit {
  id: number;
  brand_name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  text_color: string;
  bg_color: string;
  font_heading: string;
  font_body: string;
  footer_address: string;
  unsubscribe_url: string;
  social_links: Array<{ label: string; href: string }>;
  updated_at: string;
}

export interface FlowStudioAsset {
  id: number;
  name: string;
  url: string;
  mime: string | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface FlowStudioRichTemplate {
  id: string;
  channel: "email" | "sms" | "push";
  name: string;
  description: string | null;
  category: string;
  is_starter: boolean;
  thumbnail_url: string | null;
  payload: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Task #2767 — timestamp of the most recent auto-rewrite that swapped
   * legacy `{{booking.id}}` references for `{{booking.shortRef}}`.
   * NULL when the template was never touched by the migration.
   */
  short_ref_migrated_at?: string | null;
}


// Task #2767 — admin-triggerable one-shot migration response shape.
export interface ShortRefMigrationReportEntry {
  id: string;
  channel: string;
  name: string;
  matches: Array<{ path: string; before: string; after: string; count: number }>;
}

export interface ShortRefMigrationResponse {
  apply: boolean;
  stats: { scanned: number; candidates: number; rewritten: number; errors: number };
  report: ShortRefMigrationReportEntry[];
}


// Task #729 — Bookings at risk
export interface BookingsAtRiskTrip {
  id: number; public_id: string;
  offer_expiration_date?: string | null;
  started_at?: string | null;
  drop_off?: string | null;
  status?: string;
  installment_2_status?: string | null;
  renter_id?: string;
  owner_id?: string;
}

export interface ClaimDeadlineRow {
  id: number; claim_id: string; trip_id: string;
  deadline_at: string; reminders_sent: number[] | null;
}

export interface RefundRetryRow {
  id: number; trip_id: string; payment_intent: string;
  amount: number; currency: string; status: string;
  attempt_count: number; last_error: string | null; next_attempt_at: string | null;
}

export interface PayoutHoldRow {
  id: number; trip_id: string; host_id: string; region: string;
  first_held_at: string; recheck_count: number; status: string;
}

export interface LifecycleLogRow {
  id: number; job: string; trip_id: string | null; outcome: string;
  detail: Record<string, unknown> | null; created_at: string;
  /** Customer-facing trip UUID, joined server-side so admin tables can render
   *  the short hex ref instead of the internal numeric id (task #2388). */
  trip_public_id?: string | null;
}

// Task #884 — War-room unified triage item. The server returns one entry
// per (bucket, item_key) so the UI can show a single ranked list with
// SLA chips, reason chips, and a single suggested-action button.
export type BarBucket =
  | "request_expiring"
  | "expiry_overdue"
  | "unverified_pretrip"
  | "stale_active"
  | "unpaid_installment"
  | "open_claim"
  | "refund_retry"
  | "payout_hold"
  | "fraud_review";

export type BarSeverity = "red" | "orange" | "yellow" | "none";

export type BarSuggestedAction =
  | "nudge_request"
  | "cancel_request"
  | "send_verification_reminder"
  | "send_checkout_reminder"
  | "send_installment_reminder"
  | "send_claim_reminder"
  | "retry_refund_now"
  | "review_payout_hold"
  | "review_fraud"
  // Task #5387 — record a "call to confirm" phone-call outcome (operator log;
  // no money/status change).
  | "log_confirmation_call";

// Task #5387 — outcome of a "call to confirm" phone call on a pending booking.
export type CallConfirmationOutcome =
  | "confirmed"
  | "no_answer"
  | "call_back"
  | "wants_cancel";

export interface BookingsAtRiskItem {
  bucket: BarBucket;
  item_key: string;
  entity_type: "trip" | "claim" | "refund" | "payout";
  entity_id: number | string;
  trip_id: number | null;
  trip_public_id: string | null;
  renter_id: string | null;
  owner_id: string | null;
  car_id: number | null;
  title: string;
  subtitle: string | null;
  reason_code: string;
  reason_label: string;
  sla_at: string | null;
  severity: BarSeverity;
  suggested_action: BarSuggestedAction;
  snoozed_until: string | null;
  assignee_id: string | null;
  assigned_at: string | null;
  escalated_at: string | null;
  last_action: string | null;
  last_action_at: string | null;
  last_actor_id: string | null;
  // Task #5387 — inline phone numbers for the "call to confirm" workflow
  // (renter always; host only on host-facing pending buckets). Null where the
  // bucket doesn't surface the call workflow or no number is on file.
  renter_phone?: string | null;
  owner_phone?: string | null;
  // Task #5387 — most-recent call outcome for the "called X ago" indicator.
  last_call_outcome?: string | null;
  last_call_note?: string | null;
  last_call_at?: string | null;
  last_call_by?: string | null;
  raw: Record<string, unknown>;
}

export interface BookingsAtRiskUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

// Task #4661 — one `cars_images` row as embedded on admin booking surfaces.
// The shared array-aware `carPhotoUrl` selector reads these to pick the hero.
export interface AdminCarImage {
  id: number;
  url: string | null;
  image: string | null;
  r2_path: string | null;
  cdn_thumbnail: string | null;
  cdn_medium: string | null;
  cdn_full: string | null;
  is_active: boolean | null;
  display_order: number | null;
}

// Task #4661 — small car slice keyed by car_id in the bookings-at-risk
// response, so the war-room / CS booking lists can render the same car
// thumbnail as the Trips list and User-360 drill-down via `carPhotoUrl`.
export interface BookingsAtRiskCar {
  id: number;
  make: string | null;
  model: string | null;
  year: number | null;
  primary_image: AdminCarImage[] | null;
}

// Task #866: held trip (verification_hold=true) with renter licence still
// pending — surfaced in the new "Awaiting licence verification" bucket of
// the war-room admin section.
export interface AwaitingLicenceVerificationRow {
  id: number;
  public_id: string;
  renter_id: string;
  started_at: string | null;
  created_at: string;
  status: string;
  reminder_count: number;
  last_reminder_at: string | null;
  hours_since_last_reminder: number | null;
}

export interface BookingsAtRisk {
  generatedAt: string;
  filters?: {
    region: "us" | "row" | null;
    booking_type: "instant" | "request" | null;
    rental_type: "self_drive" | "chauffeur" | "event" | null;
    include_snoozed: boolean;
    assignee: string | null;
    reason: string | null;
  };
  items: BookingsAtRiskItem[];
  bucketCounts: Record<string, number>;
  severityCounts: { red: number; orange: number; yellow: number; none: number };
  total: number;
  users: Record<string, BookingsAtRiskUser>;
  // Task #4661 — car slice keyed by car_id for the booking-list thumbnails.
  cars: Record<number, BookingsAtRiskCar>;
  admins: AdminDirectoryEntry[];
  // Backwards-compat (legacy callers still consume these arrays).
  requestsExpiringSoon: BookingsAtRiskTrip[];
  unverifiedPretrip: BookingsAtRiskTrip[];
  staleActive: BookingsAtRiskTrip[];
  unpaidInstallments: BookingsAtRiskTrip[];
  awaitingLicenceVerification: AwaitingLicenceVerificationRow[];
  openClaims: ClaimDeadlineRow[];
  refundRetries: RefundRetryRow[];
  payoutHolds: PayoutHoldRow[];
  recentLog: LifecycleLogRow[];
  counters: Record<string, Record<string, number>>;
}

export interface BookingsAtRiskFilters {
  region?: "us" | "row" | null;
  bookingType?: "instant" | "request" | null;
  rentalType?: "self_drive" | "chauffeur" | "event" | null;
  /** Include items that the operator snoozed. Defaults to false server-side. */
  includeSnoozed?: boolean;
  /** "me" / "unassigned" / userId. */
  assignee?: string | null;
  /** A reason_code from the items list (e.g. "request_expiring"). */
  reason?: string | null;
}


export type AtRiskStateOp =
  | "snooze" | "clear_snooze"
  | "assign" | "unassign"
  | "escalate" | "clear_escalation";


export interface AtRiskStateMutation {
  op: AtRiskStateOp;
  items: Array<{ bucket: string; item_key: string }>;
  /** snooze: minutes ahead (1..43200). */
  minutes?: number;
  /** assign: target admin user id. */
  assignee_id?: string;
}


export interface AtRiskStateResponse {
  ok: boolean;
  count: number;
  /** Prior state per item (one row aligned with `items`); null if none. */
  prior: Array<Record<string, unknown> | null>;
}


export interface AtRiskActionRequest {
  action: BarSuggestedAction;
  items: Array<{ bucket: string; item_key: string; trip_id?: number | null; renter_id?: string | null; owner_id?: string | null }>;
  /** log_confirmation_call: required outcome of the phone call. */
  outcome?: CallConfirmationOutcome;
  /** log_confirmation_call: optional free-text note. */
  note?: string | null;
}


export interface AtRiskActionResponse {
  ok: boolean;
  action: BarSuggestedAction;
  results: Array<{
    item_key: string;
    ok: boolean;
    error?: string;
    undoable: boolean;
    inserted?: { notifications?: Array<number | string>; reminders?: Array<number | string> };
  }>;
}


export interface AtRiskUndoRequest {
  notifications?: Array<number | string>;
  reminders?: Array<number | string>;
}

export interface AtRiskUndoResponse {
  ok: boolean;
  deletedNotifications: number;
  deletedReminders: number;
}


// ─── Admin Shell Foundation (Task #883) ──────────────────────────────────

export interface AdminDirectoryEntry {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  admin_role: number | null;
  admin_role_id: number | null;
}


export interface GlobalSearchTrip { id: number; public_id: string; status: string; created_at: string; renter_id: string | null; owner_id: string | null; car_id: number | null }

export interface GlobalSearchUser { id: string; email: string | null; first_name: string | null; last_name: string | null; avatar_url: string | null; created_at: string }

export interface GlobalSearchCar { id: number; make: string; model: string; year: number; slug: string | null; listing_status: string; owner_id: string }

export interface GlobalSearchResponse {
  trips: GlobalSearchTrip[];
  users: GlobalSearchUser[];
  cars: GlobalSearchCar[];
  errors?: { trips: string | null; users: string | null; cars: string | null };
}


export interface AuditEntry {
  id: number;
  entity_type: string;
  entity_id: string;
  action: string;
  changed_by: string | null;
  // Task #2181 — server-resolved display name for the actor (admin name or
  // email fallback). Optional so older callers/responses still type-check.
  changed_by_name?: string | null;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  created_at: string;
  impersonation_session_id?: string | null;
}


export interface MessageTemplate {
  id: number;
  slug: string;
  name: string;
  category: string;
  scope: string;
  locale: string;
  subject: string | null;
  body: string;
  variables: string[];
  is_archived: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export type MessageTemplatePayload = Partial<Pick<MessageTemplate, "slug" | "name" | "category" | "scope" | "locale" | "subject" | "body" | "is_archived">> & { variables?: string[] };


export interface AdminPrefs {
  user_id: string;
  theme: "light" | "dark" | "system";
  sidebar_collapsed: boolean;
  recents: Array<{ kind: string; id: string; label: string; href: string; at: string }>;
  pinned_sections: string[];
  /**
   * Task #3059 — group keys the admin has explicitly collapsed in the
   * sidebar. Empty array means "use the tier-based defaults". A group
   * containing the active section is always force-expanded by the
   * client regardless of this list.
   */
  collapsed_groups: string[];
  updated_at: string;
}


export interface SavedView {
  id: number;
  user_id: string;
  section: string;
  name: string;
  query: string;
  is_shared: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}


export interface RiskScoreBreakdown { label: string; weight: number; detail?: string }

export interface RiskScoreResult {
  score: number;
  level: "low" | "medium" | "high";
  breakdown: RiskScoreBreakdown[];
  computed_at: string;
}


// ─── Task #888 — Listings moderation overhaul (client wrappers) ───────────
export interface ListingDiffEditor { id: string; first_name: string | null; last_name: string | null; email: string | null }

export interface ListingDiffChange {
  field: string;
  old: unknown;
  new: unknown;
  first_changed_at: string;
  last_changed_at: string;
  editors: ListingDiffEditor[];
}

export interface ListingDiffResponse {
  listing_id: number;
  last_approved_at: string | null;
  cutoff: string | null;
  has_prior_approval: boolean;
  listing_status: string;
  changes: ListingDiffChange[];
}


export interface ListingTrustOwner {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  profile_image: string | null;
  identity_verified: boolean;
  renter_verified: boolean;
  email_verified: boolean;
  phone_verified: boolean;
  host_profile_moderation_status: string | null;
  created_at: string | null;
}

export interface ListingTrustStats {
  approved_listings: number;
  rejected_listings: number;
  active_listings: number;
  average_rating: number | null;
  total_reviews: number;
  all_star: boolean;
  account_age_days: number | null;
  prior_moderation_issues: number;
}

export interface ListingTrustResponse { owner: ListingTrustOwner | null; stats: ListingTrustStats }


export interface ListingPhotoQualityImage {
  image_id: number;
  is_primary: boolean;
  score: number;
  flag_count: number;
  high: number;
  warning: number;
  info: number;
  categories: string[];
}

export interface ListingPhotoQualityResponse {
  listing_id: number;
  image_count: number;
  overall_score: number | null;
  recommend_request_better_photos: boolean;
  last_scanned_at: string | null;
  images: ListingPhotoQualityImage[];
}


// ─── Vehicle lifecycle (Task #888 — listings moderation overhaul) ──────────
// Post-approval operational state machine: listed → snoozed → delisted →
// archived. Each transition carries a structured `reason_code` that the
// queue can filter on, plus an optional free-text note.

export type VehicleLifecycleState = "listed" | "snoozed" | "delisted" | "archived";


export interface VehicleLifecycleReason { code: string; label: string; description?: string }


export interface CarLifecycle {
  car_id: number;
  state: VehicleLifecycleState;
  reason_code: string | null;
  reason_note: string | null;
  changed_by: string | null;
  changed_at: string;
}


export interface CarLifecycleEvent {
  id: number;
  car_id: number;
  prev_state: string | null;
  state: VehicleLifecycleState;
  reason_code: string | null;
  reason_note: string | null;
  changed_by: string | null;
  created_at: string;
}


// ─── Payouts hub (Task #893) ──────────────────────────────────────────────

// Task #923 — pending payout liability buckets surface the platform's
// outstanding host obligations under the Separate Charges and Transfers
// model. Each bucket reports `byCurrency` (raw amounts in the trip's
// settlement currency) plus `totalUSD` (best-effort cross-currency
// conversion for at-a-glance display).
export interface PendingLiabilityBucket {
  byCurrency: Record<string, number>;
  totalUSD: number;
}

export interface PendingLiability {
  totalUSD: number;
  byCurrency: Record<string, number>;
  byBucket: {
    confirmed_in_window: PendingLiabilityBucket;
    admin_approvals: PendingLiabilityBucket;
    failed_payouts_after_transfer: { affectedHosts: number };
  };
  // Task #933: FX provenance — added on the server in `lib/pending-liability.ts`.
  // Optional for backwards-compatibility with snapshots persisted before the
  // helper was extracted; both /admin/payouts/summary and
  // /admin/reports/financial populate it on every fresh response.
  fx?: {
    source: string;
    fetchedAt: string;
    stale: boolean;
    missingCurrencies: string[];
  };
}


export interface PayoutSummary {
  historicalReconciliationCount?: number;
  failedCount: number;
  heldByConnectCount: number;
  heldByAdminCount: number;
  /** Task #6338 — legacy pre-cutover (drop_off < 2026-06-25) admin-held trips, reported separately from the actionable count. */
  heldAdminLegacyCount?: number;
  /** Task #6656 — all-time count of completed trips with payout_status=1 (pending transfer). */
  inPayoutQueue: number;
  /** Task #6656 — count of payout_approvals rows with status='pending'. */
  pendingApprovals: number;
  /** Completed platform-payout trips still inside the actual 48-hour
   * post-completion settlement delay. Mirrors `settlementHoldItems`. */
  inSettlementHold?: number;
  /** Completed platform-payout trips where a pending approval now owns the
   * payout. Mirrors `pendingApprovalItems`; this is narrower than the full
   * approval queue count above, which also includes other payout rails. */
  pendingApprovalPayouts?: number;
  /** Completed platform-payout trips past the delay with no pending approval.
   * Mirrors `overdueItems` and needs an operator investigation. */
  overduePayouts?: number;
  pendingLiability?: PendingLiability;
}


export interface PayoutCalendarDay {
  date: string;
  count: number;
  totals: Record<string, number>;
  held: number;
}

export interface PayoutRetryState {
  status: "eligible" | "queued" | "paid" | "review_required";
  retryAllowed: boolean;
  reason: string;
  message: string;
  rejectedAttemptId: number | null;
  latestWorker: { at: string; outcome: string; reason: string | null } | null;
}

export interface PayoutHistoricalEvidence {
  state: "paid" | "unresolved" | "unavailable";
  method: "transfer" | "manual" | "payout_link" | null;
  amount: number | null;
  currency: string | null;
  needsReview?: boolean;
}

export interface PayoutHistoryDecision {
  category: "terminal_history" | "provenance_review";
  reason: "archived_import" | "terminal_import" | "identity_conflict" | "authority_unavailable" | "unmarked_import";
}
export interface PayoutHistoryItem {
  trip_id: number;
  public_id: string;
  decision: PayoutHistoryDecision;
  newPayoutRequired: false;
  evidence?: {
    issues: Array<"reversal" | "identity_conflict" | "currency_conflict" | "duplicate_movement" | "manual_adjusted" | "amount_mismatch" | "missing_movement" | "unavailable">;
    recordedByCurrency: Record<string, number> | null;
    recordedMovementCount: number | null;
    finalSettlement: "established_in_records" | "not_established" | "unavailable";
    providerVerified: false;
  };
}

export interface PayoutCalendarItem {
  historical_payout?: PayoutHistoricalEvidence;
  retry?: PayoutRetryState;
  trip_id: number;
  public_id: string;
  host_id: string;
  payout_date: string | null;
  drop_off: string | null;
  /** System time that the trip transitioned to completed. Used to classify
   * unpaid completed payouts truthfully against the 48-hour policy. */
  completed_at?: string | null;
  amount: number;
  currency: string;
  payout_status: number;
  region: string | null;
  /** Task #6563 — true when the trip's drop_off is before the 2026-06-25
   *  Connect-only cutover (or drop_off is null). These are pre-cutover legacy
   *  US DriveShare holds that will never be resolved via Connect. */
  isLegacy?: boolean;
  /** Task #6656 — host display name (joined server-side). */
  host_name?: string | null;
  /** Task #6656 — host email (joined server-side). */
  host_email?: string | null;
  /** Task #6772 — live trip status for forecast items ("confirmed" |
   *  "in_progress"). Absent on all non-forecast items. */
  trip_status?: string | null;
  /** Task #6773 — ISO timestamp of the most recent admin "Send link" action
   *  for this host's Connect onboarding nudge. Null when no link has been
   *  sent yet. Only present on connectPendingItems. */
  last_connect_link_sent_at?: string | null;
  /** Present on the completed unpaid payout overview lanes only. */
  overview_state?: "settlement_hold" | "pending_approval" | "overdue";
}

export interface PastPayoutItem {
  billing_id: number;
  trip_id: number | null;
  /** Trip UUID joined server-side so the past-payouts list renders the
   *  customer-facing short ref instead of the internal numeric trip id
   *  (task #2388). */
  trip_public_id?: string | null;
  host_id: string | null;
  /** Task #6656 — host display name (joined server-side via auth_user_id). */
  host_name?: string | null;
  /** Task #6656 — host email (joined server-side via auth_user_id). */
  host_email?: string | null;
  payout_date: string;
  amount: number;
  currency: string;
  stripe_object_id: string | null;
  region: string | null;
  /** Round-7 review fix (Task #1092 Phase B): the rail this payout
   *  used. "connect" = billing op=3 (Stripe Connect transfer),
   *  "payout_link" = billing op=4 (Stripe Payout Link completion),
   *  "off_platform" = no Stripe object id — a recorded settlement for a
   *  legacy/imported booking paid off-platform (Task #6215),
   *  "other" = defensive fallback for unexpected operation codes. */
  method: "connect" | "payout_link" | "off_platform" | "other";
  /** Task #6215 — true when this row moved no money through Stripe
   *  (stripe_object_id IS NULL): a recorded off-platform settlement, not a
   *  genuine Stripe payout. The UI groups these out of the headline total. */
  is_off_platform: boolean;
  operation: number | null;
}

/** Task #6215 — past-lane day bucket. `totals` holds ONLY genuine Stripe
 *  payouts (money that actually moved), bucketed by the row's real currency.
 *  Off-platform recorded settlements are reported separately in
 *  `offPlatformTotals`/`offPlatformCount` and never summed into `totals`. */
export interface PastPayoutDay {
  date: string;
  count: number;
  totals: Record<string, number>;
  stripeCount: number;
  offPlatformCount: number;
  offPlatformTotals: Record<string, number>;
}

export interface PayoutCalendarResponse {
  historicalReconciliationItems?: PayoutHistoryItem[];
  from: string;
  to: string;
  region: string | null;
  upcoming: PayoutCalendarDay[];
  past: PastPayoutDay[];
  upcomingItems: PayoutCalendarItem[];
  pastItems: PastPayoutItem[];
  /** Task #6515 — all payout_status=3 trips without date cap, returned when
   *  includeAllHeld=true. The frontend renders these at the top of the
   *  upcoming lane under "Admin hold". */
  heldItems?: PayoutCalendarItem[];
  /** Task #6646 — payout_status=4 (Connect-pending) completed trips, always
   *  returned. Rendered at the very top of the calendar as "Awaiting host
   *  account setup" — no KPI tile click required. */
  connectPendingItems?: PayoutCalendarItem[];
  /** Task #6772 — confirmed/in_progress trips whose drop_off falls in the
   *  upcoming window. Rendered as a "Forecast" lane below the settlement queue.
   *  These items carry `trip_status` ("confirmed" | "in_progress") and are NOT
   *  counted in the Window total. */
  forecastItems?: PayoutCalendarItem[];
  /** Completed platform-payout trips still within the real 48-hour delay.
   * These rows are mutually exclusive with `pendingApprovalItems` and
   * `overdueItems`. */
  settlementHoldItems?: PayoutCalendarItem[];
  /** Completed platform-payout trips with a pending manual approval. Money
   * does not move until an administrator decides in the Approvals flow. */
  pendingApprovalItems?: PayoutCalendarItem[];
  /** Completed platform-payout trips past the delay that have no pending
   * approval. These require investigation rather than more waiting. */
  overdueItems?: PayoutCalendarItem[];
  /** Task #7890 — payout_status=4 trips whose payout failed because the
   *  platform's Stripe balance was insufficient (`platform_balance_insufficient`
   *  reason in payout_connect_holds). These are surfaced in a distinct
   *  "BLOCKED — INSUFFICIENT PLATFORM BALANCE" section above "Awaiting host
   *  account setup" so finance knows to top up the Stripe balance and then
   *  re-trigger from the trip drawer, rather than sending a Connect link. */
  balanceBlockedItems?: PayoutCalendarItem[];
}

/** Task #6515 — a single open Connect hold from the payout_connect_holds table. */
export interface PayoutConnectHoldItem {
  id: number;
  trip_id: number | null;
  trip_public_id: string | null;
  host_id: string | null;
  host_name: string | null;
  host_email: string | null;
  reason: string | null;
  region: string | null;
  first_held_at: string | null;
  last_checked_at: string | null;
  recheck_count: number | null;
}


export interface PayoutTripDetail {
  retry?: PayoutRetryState;
  trip: {
    historical_payout?: PayoutHistoricalEvidence;
    payout_history?: PayoutHistoryDecision | null;
    id: number;
    public_id: string;
    status: string;
    payout_status: number;
    drop_off: string | null;
    started_at: string | null;
    // Task #6219 — pickup timestamp so the drawer's booking-summary can show
    // the rental window, not just the payout-relevant drop-off.
    pick_up: string | null;
    total_price: number | null;
    owner_earn: number | null;
    service_fee: number | null;
    currency: string;
    estimated_payout_at: string | null;
  };
  host: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    country_code: string | null;
  } | null;
  // Task #6219 — the renter and car so the payout drawer can render the full
  // booking context (who rented what), not just the payee.
  renter?: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  car?: {
    id: number;
    make: string | null;
    model: string | null;
    year: number | null;
  } | null;
  region: string;
  billing: Array<Record<string, unknown>>;
  // Task #6219 — a derived renter-payment summary (op=1 billing) so the drawer
  // can state clearly whether the renter was actually charged/captured and
  // against which Stripe object, with success/failure state.
  payment?: {
    status: "captured" | "failed" | "none";
    captured: boolean;
    amount: number | null;
    currency: string | null;
    paymentIntentId: string | null;
    chargeId: string | null;
    region: string | null;
    error: string | null;
    createdAt: string | null;
    attempts: number;
  };
  holds: Array<Record<string, unknown>>;
  actions: Array<{ id: number; kind: string; amount: number | null; currency: string | null; reason: string; admin_id: string; created_at: string; metadata: Record<string, unknown> | null }>;
  stripeTransfer: Record<string, unknown> | null;
  /** Round-8 fix: backend `/admin/payouts/calendar/trip/:tripId`
   *  returns a `connect` block with `account_id` (the Stripe Connect
   *  account id, sourced from `users.stripe_ext_account_id` and
   *  enriched with live payouts_enabled/charges_enabled/requirements
   *  best-effort). The host-card "Connect:" row reads
   *  `data.connect?.account_id` rather than a phantom field on the
   *  `host` object. */
  connect?: {
    account_id?: string | null;
    region?: string;
    payouts_enabled?: boolean | null;
    charges_enabled?: boolean | null;
    requirements?: Record<string, unknown> | null;
    error?: string;
  };
}


export interface PayoutHostListItem {
  host_id: string;
  name: string;
  email: string | null;
  country_code: string | null;
  stripe_ext_account_id: string | null;
  lifetime: { amount: number; currency: string };
  pending: { amount: number; currency: string; held: number };
  lastPayoutAt: string | null;
  onHold: boolean;
  // Task #1092 Phase B — resolved payout method for this host.
  payoutMethod?: "connect" | "payout_link" | "manual" | "awaiting_setup";
  payoutTier?: number | null;
}


export interface AdminPayoutLinkItem {
  id: number;
  trip_id: string;
  status: "pending" | "sent" | "completed" | "failed" | "expired" | "cancelled";
  amount: number;
  currency: string;
  stripe_region: string;
  stripe_payout_link_id: string | null;
  url: string;
  retry_count: number;
  last_error: string | null;
  created_at: string;
  sent_at: string | null;
  expires_at: string;
  completed_at: string | null;
  failed_at: string | null;
}


export interface PayoutHostDetail {
  host: { id: string; email: string | null; name: string; country_code: string | null };
  lifetime: Record<string, number>;
  pending: Array<{ trip_id: number; public_id: string; drop_off: string | null; estimated_payout_at: string | null; payout_status: number; amount: number; currency: string }>;
  pendingByCurrency: Record<string, { amount: number; held: number; count: number }>;
  recentBilling: Array<Record<string, unknown>>;
  openHolds: Array<Record<string, unknown>>;
  actions: Array<{ id: number; kind: string; trip_id: number | null; amount: number | null; currency: string | null; region: string | null; reason: string; admin_id: string; created_at: string; metadata: Record<string, unknown> | null }>;
  connect: Record<string, unknown>;
}


export interface PayoutFailureItem {
  billing_id: number;
  trip_id: number | null;
  trip_public_id: string | null;
  trip_status: string | null;
  host_id: string | null;
  host: { id: string; email: string | null; first_name: string | null; last_name: string | null; country_code: string | null } | null;
  amount: number;
  currency: string;
  stripe_object_id: string | null;
  stripe_region: string | null;
  stripe_error: string | null;
  failed_at: string;
}

/** Read-only operational evidence. The API deliberately excludes quote tokens,
 * Stripe object ids, renter/host personal data, payouts, and refunds. */
export interface CustomQuotePaymentAttempt {
  billing_id: number;
  status: "succeeded" | "failed";
  amount: number;
  currency: string;
  attempted_at: string;
  error: string | null;
}

export interface CustomQuotePaymentEvidenceItem {
  quote_id: number;
  quote_status: string;
  trip_id: number;
  trip_public_id: string | null;
  trip_status: string | null;
  rental_type: string;
  cancellation_policy: string | null;
  non_refundable_base_rate: number;
  delivery_fee_override: number | null;
  quote_currency: string | null;
  quote_created_at: string;
  quote_consumed_at: string | null;
  successful_payment: CustomQuotePaymentAttempt;
  payment_attempt_count: number;
  payment_errors: Array<Pick<CustomQuotePaymentAttempt, "attempted_at" | "error">>;
}

export interface CustomQuotePaymentEvidenceResponse {
  items: CustomQuotePaymentEvidenceItem[];
  page: number;
  pageSize: number;
  total: number;
  matched_total: number;
  window_started_at: string;
  window_ends_at: string;
}

export interface CustomQuotePaymentEvidenceDetail {
  trip: { id: number; public_id: string | null; status: string | null };
  quote: {
    id: number;
    trip_id: number;
    status: string;
    rental_type: string;
    rental_fee_override: number;
    delivery_fee_override: number | null;
    cancellation_policy: string | null;
    currency_code: string | null;
    created_at: string;
    consumed_at: string | null;
  };
  successful_payment: CustomQuotePaymentAttempt | null;
  payment_attempt_count: number;
  payment_attempts: CustomQuotePaymentAttempt[];
}


// Task #4093 — owner-payout drift reconciliation queue.
export interface PayoutDriftItem {
  trip_id: number;
  trip_public_id: string | null;
  trip_status: string | null;
  payout_status: number | null;
  host_id: string | null;
  host: { id: string; email: string | null; first_name: string | null; last_name: string | null; country_code: string | null } | null;
  original_amount: number;
  recomputed_amount: number;
  delta: number;
  currency: string;
  detected_at: string;
  resolved: boolean;
  resolution: {
    kind: "drift_reconcile_pay" | "drift_resolve";
    reason: string | null;
    amount: number | null;
    currency: string | null;
    admin_id: string | null;
    created_at: string;
  } | null;
}

export interface PayoutDriftResponse {
  items: PayoutDriftItem[];
  total: number;
  unresolved: number;
}

export interface PayoutDriftReconcileResult {
  ok: true;
  actionId: number | null;
  action: "pay_delta" | "resolve";
  transferId?: string;
  billingId?: number | null;
  delta?: number;
  currency?: string;
}

// Task #4796 — manual payout-approval gate (feature_payout_manual_approval).
// One queued obligation an admin reviews before any host payout fires.
export type PayoutApprovalRail = "connect" | "payout_link" | "drift";
export type PayoutApprovalStatus = "pending" | "approved" | "executing" | "paid" | "held";

export interface PayoutApprovalItem {
  id: number;
  rail: PayoutApprovalRail;
  status: PayoutApprovalStatus;
  tripId: number | null;
  publicId: string | null;
  amount: number | null;
  amountMinor: number | null;
  currency: string | null;
  region: string | null;
  hostId: string | null;
  hostEmail: string | null;
  hostName: string | null;
  destination: string | null;
  recipientId: string | null;
  payoutLinkId: number | null;
  driftLogId: number | null;
  requestedAt: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  paidObject: string | null;
  paidAt: string | null;
  /** Task #6296 — last settle failure (payout_link rail): machine reason +
   *  timestamp of the most recent failed funding attempt, or null. */
  settleFailureReason: string | null;
  settleFailedAt: string | null;
  /** The booking's drop-off date/time, or null if not linked or unknown. */
  dropOff: string | null;
}

export interface PayoutApprovalsResponse {
  items: PayoutApprovalItem[];
  count: number;
}


// Task #923 — drill-down for the Pending Liability card.
// Task #6656 — by-host endpoint uses a superset of the summary buckets so
// confirmed_in_window stays pre-trip only; additional post-trip states get
// their own keys so the drilldown doesn't overstate "Confirmed (pre-trip)".
export type PendingLiabilityBucketKey =
  | "confirmed_in_window"
  | "completed_in_48h_hold"
  | "admin_held"
  | "connect_pending"
  | "admin_approvals";

export interface PendingLiabilityHostRow {
  host_id: string;
  host: { first_name: string | null; last_name: string | null; email: string | null } | null;
  totalUSD: number;
  byCurrency: Record<string, number>;
  byBucket: Partial<Record<PendingLiabilityBucketKey, number>>;
  trips: Array<{ trip_id: string; public_id: string; bucket: PendingLiabilityBucketKey; owner_earn: number; currency: string; drop_off: string | null }>;
}

export interface PendingLiabilityByHost {
  hosts: PendingLiabilityHostRow[];
  count: number;
}


// ─── Task #1091 — Payout Routing Phase A admin API ───────────────────────

export type PayoutTier = 1 | 2 | 3 | 4;

export type PayoutMethod = "connect" | "payout_link" | "manual" | "awaiting_setup";


export interface PayoutPolicyCountryRow {
  code: string;
  enabled: boolean;
  paymentType: "full" | "partial";
  // tier === derivedTier always — tier is strictly derived from
  // connectRequired/payoutLinkAllowed/manualPayoutAllowed +
  // Stripe capabilities. Not editable independently.
  tier: PayoutTier;
  derivedTier: PayoutTier;
  connectRequired: boolean;
  payoutLinkAllowed: boolean;
  manualPayoutAllowed: boolean;
  connectSupported: boolean | null;
  globalPayoutsSupported: boolean | null;
  capabilityRefreshedAt: string | null;
}


export interface PayoutPolicyCountryUpdate {
  // Tier is strictly derived from these three booleans — not editable.
  // Sending `payoutTier` will be rejected by the API with a 400.
  connectRequired?: boolean;
  payoutLinkAllowed?: boolean;
  manualPayoutAllowed?: boolean;
}


export interface CapabilityRefreshResult {
  ok: true;
  attempted: number;
  upserted: number;
  skipped: number;
  errors: string[];
  source: "stripe" | "skipped_no_stripe";
}


export interface HostPayoutOverrideResponse {
  hostId: string;
  countryCode: string;
  overrides: {
    requireConnect: boolean | null;
    allowPayoutLink: boolean | null;
    allowManual: boolean | null;
  };
  country: {
    // No `payoutTier` field — tier is strictly derived. Read
    // `decision.tier` if you need the resolver-computed tier.
    connectRequired: boolean | null;
    payoutLinkAllowed: boolean | null;
    manualPayoutAllowed: boolean | null;
  };
  capability: { connectSupported: boolean | null; globalPayoutsSupported: boolean | null };
  decision: {
    countryCode: string;
    tier: PayoutTier;
    payoutMethod: PayoutMethod;
    requiresConnect: boolean;
    allowsPayoutLink: boolean;
    allowsManual: boolean;
    source: "country" | "host_override" | "fallback";
    reason: string;
    hostOverrideApplied: boolean;
    methodLabel: string;
  };
}


export interface HostPayoutOverrideUpdate {
  requireConnect?: boolean | null;
  allowPayoutLink?: boolean | null;
  allowManual?: boolean | null;
}


// ──────────────────────────────────────────────────────────────────────────
// Task #1093 — Payout Routing Phase C: manual payout queue + approval.
// ──────────────────────────────────────────────────────────────────────────

export type ManualPayoutMethod =
  | "wire" | "ach" | "sepa" | "wise" | "paypal" | "cheque" | "cash" | "other";


export interface ManualPayoutQueueItem {
  hold_id: number;
  trip_id: number;
  trip_public_id: string | null;
  host_id: string;
  host_name: string;
  host_email: string | null;
  country_code: string | null;
  region: string | null;
  first_held_at: string;
  age_days: number;
  expected_amount: number;
  expected_currency: string;
  // Task #1093 (review fix) — USD equivalent of expected_amount so the
  // queue table can show a consistent USD column alongside the local
  // amount. Null only if the FX snapshot lacks the row's currency.
  expected_amount_usd: number | null;
  fx_rate_to_usd: number | null;
  reason: string | null;
  is_aging: boolean;
}


export interface ManualPayoutDetail {
  trip: {
    id: number;
    public_id: string | null;
    payout_status: number | null;
    drop_off: string | null;
    completed_at: string | null;
  };
  host: {
    id: string;
    name: string;
    email: string | null;
    country_code: string | null;
  } | null;
  hold: {
    id: number;
    status: string;
    region: string | null;
    reason: string | null;
    first_held_at: string;
  } | null;
  expected: {
    amount: number;
    currency: string;
    fx_rate_to_usd: number;
    amount_usd: number | null;
  };
  payment_methods: ManualPayoutMethod[];
  prior_payouts: Array<{
    id: number;
    amount: number;
    currency: string;
    payment_method: string;
    payment_reference: string;
    paid_at: string;
    has_discrepancy: boolean;
    discrepancy_reason: string | null;
    approved_by: string;
    created_at: string;
  }>;
}


export interface ManualPayoutApproveRequest {
  amount: number;
  currency: string;
  payment_method: ManualPayoutMethod;
  // Task #1093 (review fix) — required when payment_method = "other";
  // free-text label of the actual rail (e.g. "Revolut", "Western Union").
  payment_method_other?: string;
  payment_reference: string;
  paid_at: string;
  notes?: string;
  // Required for non-USD payouts (form auto-fills it but the admin can
  // override with their bank's actual rate).
  fx_rate_to_usd?: number | null;
  flag_discrepancy?: boolean;
  discrepancy_reason?: string;
}


// ════════════════════════════════════════════════════════════════════════
// Disputes / Fraud cases / Manual KYC review (Task #1046)
// ════════════════════════════════════════════════════════════════════════

export interface DisputeRow {
  id: number;
  source: "stripe" | "internal" | string;
  external_id: string | null;
  trip_id: number | null;
  /** Joined trip UUID so the admin disputes table/drawer can render the
   *  customer-facing short ref instead of the internal numeric id
   *  (task #2388). */
  trip_public_id?: string | null;
  /** Task #4661 — the trip's car (with array-aware primary_image embed) so the
   *  disputes table can render the same car thumbnail as other admin booking
   *  surfaces. Null when the dispute has no trip or the trip has no car. */
  car?: {
    id: number;
    make: string | null;
    model: string | null;
    year: number | null;
    primary_image: AdminCarImage[] | null;
  } | null;
  charge_id: string | null;
  payment_intent_id: string | null;
  host_id: string | null;
  renter_id: string | null;
  amount: number | null;
  currency: string | null;
  reason: string | null;
  status: string;
  decision: string | null;
  decided_at: string | null;
  evidence_due_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DisputeDetail extends DisputeRow {
  decision_reason: string | null;
  decided_by: string | null;
  host_narrative: string | null;
  renter_narrative: string | null;
  metadata: Record<string, unknown> | null;
}

export interface DisputeEvidence {
  id: number; dispute_id: number; kind: string;
  filename: string | null; mime_type: string | null;
  storage_key: string | null; note: string | null;
  uploaded_by: string | null; created_at: string;
}

export interface DisputeDecision {
  id: number; dispute_id: number; action: string;
  reason_code: string | null; notes: string | null;
  payload: Record<string, unknown> | null;
  performed_by: string; created_at: string;
}


export interface FraudCaseRow {
  id: number; case_status: string; verdict: string | null;
  summary: string | null; primary_user_id: string | null;
  opened_by: string; closed_by: string | null;
  closed_at: string | null; created_at: string; updated_at: string;
}

export interface FraudCaseEntity {
  id: number; case_id: number; entity_type: string;
  entity_id: string; added_by: string; note: string | null; created_at: string;
}

export interface FraudCaseAction {
  id: number; case_id: number; action: string;
  notes: string | null; payload: Record<string, unknown> | null;
  performed_by: string; created_at: string;
}


export interface KycReviewRow {
  id: number; user_id: string; source: string; provider_ref: string | null;
  status: string; reason: string | null;
  reviewed_by: string | null; reviewed_at: string | null;
  created_at: string; updated_at: string;
}

export interface KycReviewDetail extends KycReviewRow {
  document_storage_key: string | null;
  document_filename: string | null;
  document_mime: string | null;
  override_value: boolean | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
}

export interface KycReviewUser {
  id: string; email: string | null;
  first_name: string | null; last_name: string | null;
  identity_verified: boolean | null;
  identity_verification_source: string | null;
  country_code: string | null;
}

// Task #3698 — real provider verification data surfaced alongside a manual
// review. A provider session is one Didit/MVR run from kyc_verification_log.
export interface KycProviderSession {
  id: number;
  provider: string;
  document_type: string | null;
  status: string;
  reason: string | null;
  provider_ref: string | null;
  verified_at: string | null;
  created_at: string | null;
}

// The user's driver verification record (extracted evidence + terminal state).
export interface KycDriverEvidence {
  id: number;
  license_verification_status: number | null;
  license_verification_time: string | null;
  mvr_status: string | null;
  mvr_completed_at: string | null;
  review_reason: string | null;
  review_notes: string | null;
  review_decision: string | null;
  review_routed_at: string | null;
  review_resolved_at: string | null;
  extracted_first_name: string | null;
  extracted_last_name: string | null;
  extracted_full_name: string | null;
  extracted_dob: string | null;
  extracted_age_years: number | null;
  extracted_issuing_country: string | null;
  extracted_issuing_state: string | null;
  last_didit_session_id: string | null;
  is_active: boolean | null;
  updated_at: string | null;
  created_at: string | null;
}

export interface KycReviewDetailResponse {
  review: KycReviewDetail;
  user: KycReviewUser | null;
  provider_sessions: KycProviderSession[];
  driver: KycDriverEvidence | null;
}


// ── Task #866: Driver Verification Queue + criteria ───────────────────────

export interface DriverVerificationCriteria {
  min_renter_age_years: number;
  mvr_max_minor_violations_3y: number;
  mvr_max_major_violations_3y: number;
  mvr_max_suspensions_3y: number;
  mvr_max_accidents_3y: number;
}


export interface DriverVerificationQueueRow {
  id: number;
  user_id: string;
  license_verification_status: number;
  mvr_status: string | null;
  mvr_order_id: string | null;
  mvr_completed_at: string | null;
  review_reason: string | null;
  review_notes: string | null;
  review_routed_at: string | null;
  review_resolved_at: string | null;
  review_decision: string | null;
  review_resolved_by: string | null;
  extracted_full_name: string | null;
  extracted_first_name: string | null;
  extracted_last_name: string | null;
  extracted_dob: string | null;
  extracted_age_years: number | null;
  extracted_issuing_state: string | null;
  extracted_issuing_country: string | null;
  last_didit_session_id: string | null;
  created_at: string;
  updated_at: string;
  user: { id: string; first_name: string | null; last_name: string | null; email: string | null } | null;
}


export interface DriverVerificationQueueResponse {
  queue: DriverVerificationQueueRow[];
  total: number;
  page: number;
  limit: number;
}

export interface BookingMvrAttempt {
  id: number;
  trip_id: number;
  driver_id: number;
  didit_session_id: string;
  samba_order_id: string | null;
  status: string;
  result: { decision?: string; admin_review?: { decision: string; notes: string; admin_user_id: number; reviewed_at: string; settled_at?: string } } | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface BookingMvrDetail {
  attempt: BookingMvrAttempt;
  trip: { id: number; public_id: string; status: string; verification_hold: boolean; renter_is_driver: boolean | null };
  driver: { id: number; user_id: number; extracted_full_name: string | null };
  reviewEvidence?: {
    source: "attempt_snapshot" | "order_log" | "driver_order_match" | "unavailable";
    reportSource: "order_log" | "driver_order_match" | "unavailable";
    criteriaSource: "attempt_snapshot" | "current_configuration";
    criteria: {
      min_renter_age_years: number;
      mvr_max_minor_violations_3y: number;
      mvr_max_major_violations_3y: number;
      mvr_max_suspensions_3y: number;
      mvr_max_accidents_3y: number;
    };
    counts: { minor: number; major: number; suspensions: number; accidents: number } | null;
    recordFound: boolean | null;
    isClean: boolean | null;
    decision: string | null;
    reason: string | null;
    message: string;
    violations: Array<{ severity?: string; description?: string; date?: string }> | null;
    suspensions: Array<{ startDate?: string; endDate?: string | null; reason?: string }> | null;
    accidents: Array<{ date?: string; description?: string }> | null;
    comparisons: Array<{
      metric: "minor" | "major" | "suspensions" | "accidents";
      observed: number | null; maximum: number; exceeded: boolean | null;
      consequence: "manual_review" | "automatic_rejection";
    }>;
  };
}

export interface UnboundVerificationStart {
  context_id: number;
  driver_id: number;
  driver: { first_name: string | null; last_name: string | null; email: string | null } | null;
  assignment_id: number | null;
  assignment_status: string | null;
  trip_id: number | null;
  subject: string;
  claimed_at: string;
  created_at: string;
  binding_state: "unbound";
}

export interface UnboundVerificationStartsResponse {
  starts: UnboundVerificationStart[];
  total: number;
  page: number;
  limit: number;
}


export interface DriverVerificationDetail {
  driver: Record<string, unknown> & { id: number; user_id: string };
  user: { id: string; first_name: string | null; last_name: string | null; email: string | null; phone: string | null; country_code: string | null; created_at: string } | null;
  kyc_logs: Array<Record<string, unknown>>;
  mvr_logs: Array<Record<string, unknown>>;
  audit_log: Array<Record<string, unknown>>;
  verification?: {
    contexts?: Array<Record<string, unknown> & {
      id?: number;
      driver_id?: number;
      assignment_id?: number | null;
      booking_trip_id?: number | null;
      booking_public_id?: string | null;
      subject?: string;
      didit_session_id?: string | null;
      samba_order_id?: string | null;
      created_at?: string;
    }>;
    phase?: string;
    didit_attempt_usage?: Record<string, number>;
    /** Attempt counts keyed by booking trip ID, separate from legacy renter/account usage. */
    didit_booking_attempt_usage?: Record<string, number>;
  };
}


// ── Task #866: SambaSafety admin tester ───────────────────────────────────

export interface SambaTokenInfo {
  token: string;
  expiresAt: number;
  env: "prod" | "demo";
  baseUrl?: string;
  defaultEnv?: "prod" | "demo";
}


export interface SambaMvrSubmitResult {
  orderId: string;
  status: string;
  raw?: unknown;
}


export interface SambaMvrFetchResult {
  status: string;
  raw?: unknown;
  [key: string]: unknown;
}


export interface SambaSimulateWebhookResult {
  success: boolean;
  dry_run: true;
  criteria: DriverVerificationCriteria;
  counts: { minor: number; major: number; suspensions: number; accidents: number };
  routed: {
    decision: "auto_approve" | "auto_fail" | "needs_review";
    reason: string | null;
    message?: string | null;
    shouldRunMvr?: boolean;
  };
}


export interface SambaMvrFetchResponse {
  success: boolean;
  result: SambaMvrFetchResult;
  // Routing preview — backend computes what the threshold logic *would* do
  // for this MVR record without mutating any driver row. Null when status is
  // not "completed".
  routed: {
    decision: "auto_approve" | "auto_fail" | "needs_review";
    reason: string | null;
    message?: string | null;
    shouldRunMvr?: boolean;
  } | null;
  counts: { minor: number; major: number; suspensions: number; accidents: number } | null;
  criteria: DriverVerificationCriteria;
}


// ── DIDIT admin tester ────────────────────────────────────────────────────
// End-to-end self-test of the DIDIT → Samba MVR → driver-row chain.
// Operates on the admin's *own* driver row.

export interface DiditTesterSession {
  success: boolean;
  session_id: string | null;
  verification_url: string | null;
  verification_qr: string | null;
  callback_url: string;
}


export interface DiditTesterStages {
  didit_session_created: boolean;
  didit_completed: boolean;
  licence_status: number;
  mvr_status: string | null;
  mvr_completed_at: string | null;
  review_decision: string | null;
  review_reason: string | null;
}


export interface DiditTesterStatus {
  success: true;
  user_id: string;
  phase: string;
  message: string | null;
  stages: DiditTesterStages;
  driver: Record<string, unknown> | null;
  kyc_log: Record<string, unknown> | null;
}


// ─────────────────────────────────────────────────────────────────────
// Brand requests (task #1547). Surfaces the host-driven "request a new
// make" queue to the admin UI. Verified-brand registry is the single
// source of truth, so approving here writes through to `car_makes`,
// updates every linked car's make_id/make text, and refreshes the
// in-memory registry on every API server.
// ─────────────────────────────────────────────────────────────────────

export interface BrandRequest {
  id: number;
  original_text: string;
  normalised_name: string | null;
  status: "pending" | "approved" | "rejected";
  requested_by: string | null;
  decided_by: string | null;
  decision_note: string | null;
  approved_make_id: number | null;
  created_at: string;
  decided_at: string | null;
  requester: { email: string | null; first_name: string | null; last_name: string | null } | null;
  linked_listings: number;
}


// ── Communications Flows visualizer (Task #1515) ─────────────────────
export type CommsFlowsTriggerKind = "route" | "webhook" | "event" | "cron" | "manual";

export interface CommsFlowsTrigger {
  kind: CommsFlowsTriggerKind;
  method?: string; path?: string; provider?: string; event?: string;
  cronId?: string; schedule?: string; manualLabel?: string;
}

export interface CommsFlowsChannel {
  kind: "email" | "sms" | "push" | "intercom";
  templateId: string; preview?: string; variables?: string[]; wired: boolean;
}

export interface CommsFlowsCodeRef { file: string; line: number; symbol?: string }

export interface CommsFlowsListItem {
  id: string; label: string; category: string; audience: string;
  trigger: CommsFlowsTrigger; triggerRef?: CommsFlowsCodeRef; whyItFires: string;
  channels: CommsFlowsChannel[];
  conditionalCount: number; callSiteCount: number; retryCount: number | null;
  isAdHoc?: boolean;
}

export interface CommsFlowsChannelInternals {
  smsRegionFallback?: { from: string; to: string; sourceRef: CommsFlowsCodeRef };
  pushInvalidTokenRetry?: { sourceRef: CommsFlowsCodeRef };
  pushReceiptPollRetry?: { sourceRef: CommsFlowsCodeRef };
  emailSanitizeFallback?: { sourceRef: CommsFlowsCodeRef };
  intercomEventRetry?: { sourceRef: CommsFlowsCodeRef };
}

/** Task #4901 / #4906 — go-live comms cutoff + kill-switch diagnostics. */
export interface CommsFlowsGoLiveStatus {
  cutoff: string;
  cutoffValid: boolean;
  /** Effective pause state — env kill-switch OR the dashboard override. */
  commsPaused: boolean;
  /** Env-only kill-switch state (the operator layer). */
  commsPausedEnv: boolean;
  /** Dashboard override: true/false when set, null when unset (env applies). */
  commsPausedOverride: boolean | null;
  /** The platform_config key the override is persisted under. */
  pauseConfigKey: string;
  killSwitchEnvs: { GO_LIVE_COMMS_PAUSED: boolean; BATCH_COMMS_PAUSED: boolean };
  suppressedSinceBoot: Record<string, number>;
}

export interface CommsFlowsListResponse {
  scannedAt: string; filesScanned: number;
  flows: CommsFlowsListItem[];
  adHocCount: number; orphanedCount: number;
  crons: Array<{ id: string; name: string; schedule: string; flowIds: string[] }>;
  channelInternals?: CommsFlowsChannelInternals;
  repoRoot?: string;
  /** Surfaced when the server-side scan failed and returned a degraded
   *  model. The UI shows it in a banner instead of going blank. */
  lastScanError?: string;
  /** Go-live comms cutoff + kill-switch diagnostics. */
  goLive?: CommsFlowsGoLiveStatus;
}

export interface CommsFlowsCallSite {
  channel: "email" | "sms" | "push" | "intercom" | "flow";
  callee: string; templateOrFlowId: string | null;
  predicates: string[]; retries?: number;
  codeRef: { file: string; line: number; symbol?: string };
}

export interface CommsFlowsDetail {
  flow: CommsFlowsListItem & { callSites: CommsFlowsCallSite[]; conditionals: string[]; codeRef?: { file: string; line: number; symbol?: string } };
  mermaid: string; scannedAt: string;
}


// Task #2628 — Notification delivery preview.
export type NotificationPreviewDecision =
  | "would-send"
  | "blocked-by-preference"
  | "blocked-by-allowlist"
  | "already-deduped"
  | "channel-disabled"
  | "channel-not-wired";


export interface NotificationPreviewChannel {
  channel: "email" | "sms" | "push";
  decision: NotificationPreviewDecision;
  reason: string;
  enabled: boolean;
  wired: boolean;
  templateId: string;
  preferenceKey: string | null;
  preferenceValue: boolean | null;
  allowlistOk: boolean | null;
  dedupedRunId: number | null;
  dedupedAt: string | null;
}


export interface NotificationPreviewRun {
  id: number;
  channel: string;
  status: string;
  reason: string | null;
  template_id: string | null;
  step_id: string | null;
  context_hash: string | null;
  provider_message_id: string | null;
  created_at: string;
}


export interface NotificationPreviewPushSend {
  id: number;
  user_id: string;
  device_id: number | null;
  flow_run_id: number | null;
  template_id: string;
  status: string;
  provider_ticket_id: string | null;
  provider_receipt_id: string | null;
  error_message: string | null;
  created_at: string;
}


export interface NotificationPreviewResponse {
  flowId: string;
  flowDescription: string;
  trigger: string;
  userId: string;
  contextKey: string | null;
  dedupeHash: string | null;
  environment: "production" | "non-production";
  user: { id: string; email: string | null; phone: string | null; countryCode: string | null };
  breakdown: NotificationPreviewChannel[];
  recentRuns: NotificationPreviewRun[];
  pushSends: NotificationPreviewPushSend[];
}
